import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBeforeUnload, useNavigate, useSearchParams } from 'react-router-dom';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import ReportRoundedIcon from '@mui/icons-material/ReportRounded';
import NotificationsOffRoundedIcon from '@mui/icons-material/NotificationsOffRounded';
import AlternateEmailRoundedIcon from '@mui/icons-material/AlternateEmailRounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import GppBadRoundedIcon from '@mui/icons-material/GppBadRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import PlaylistRemoveRoundedIcon from '@mui/icons-material/PlaylistRemoveRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import { LoadingButton } from '@mui/lab';
import { VariableSizeList } from 'react-window';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

import { BULK_UPLOAD_TEMPLATE_PATH } from './mock-data';
import { RootState } from '../../store';
import { api } from '../../http';
import { handleApiError } from '../../services/react-query/react-query.client';
import { permissionToText } from '../../constants/systemPermissions.constants';
import { highlightedFieldsForError, InvalidUserFieldKey } from './error-highlighting';
import {
    getRestoredBulkUploadState,
    isBlockedExecuteResult,
    normalizeExecuteResponseResult,
    type BulkUploadStep,
    type ExecutionState,
} from './execute-ui.helpers';
import { SYSTEM_USER_BULK_UPLOAD_TEST_IDS } from './test-ids';

type ExecuteResultUser = {
    email: string;
    firstName: string;
    lastName: string;
    userId: string;
};

type ExecuteFailedUser = {
    email: string;
    firstName: string;
    lastName: string;
    action: 'create' | 'update';
    error: string;
};

type ExecuteFailedInvite = {
    email: string;
    error: string;
};

type ExecuteStructureFailure = {
    type: 'group' | 'team';
    name: string;
    groupName?: string | null;
    error: string;
};

type ExecuteResult = {
    status: 'success' | 'partial' | 'failed';
    failedPhase: 'structure' | 'users' | 'invites' | null;
    previewId: string;
    executionState: ExecutionState;
    executedAt: string | null;
    counts: {
        groupsCreated: number;
        teamsCreated: number;
        usersCreated: number;
        usersUpdated: number;
        invitesAttempted: number;
        invitesSent: number;
        inviteFailures: number;
        userFailures: number;
    };
    createdUsers: ExecuteResultUser[];
    updatedUsers: ExecuteResultUser[];
    inviteResults: Array<{ email: string; sent: boolean; error: string | null; attemptedAt: string | null }>;
    failures: {
        structure: ExecuteStructureFailure[];
        users: ExecuteFailedUser[];
        invites: ExecuteFailedInvite[];
    };
    createdStructure?: {
        groups: Array<{ name: string }>;
        teams: Array<{ groupName: string; teamName: string }>;
    };
    nextAction: 'done' | 'rerun_preview_then_execute' | 'review_partial_results';
    refreshedPreview?: PreviewResult;
    users?: PreviewUser[];
    invalidUsers?: PreviewInvalidUser[];
    creationPlan?: PreviewResult['creationPlan'];
    summary?: PreviewSummary;
    message?: string;
};

type ExecuteResponse = {
    isValid: boolean;
    message: string;
    result: ExecuteResult | null;
};

type PreviewIssue = {
    code: string;
    message: string;
    fields: InvalidUserFieldKey[];
    rowNumbers: number[];
};

type DirectGroupAssignment = {
    id?: string;
    name: string;
    isNew: boolean;
};

type TeamAssignment = {
    id?: string;
    groupName: string;
    teamName: string;
    isNew: boolean;
};

type PreviewUser = {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    role: string;
    alertNotificationType: string;
    sendInvite: boolean;
    action: 'create' | 'update' | 'noop';
    existingUserId?: string;
    existingRole?: string | null;
    directGroupAssignments: DirectGroupAssignment[];
    teamAssignments: TeamAssignment[];
    desiredGroupIds: string[];
    desiredTeamIds: string[];
    sourceRowNumbers: number[];
    warnings: string[];
    updateDiff?: {
        current: {
            name: string | null;
            phone: string | null;
            role: string | null;
            alertNotificationType: string | null;
            sendInvite: boolean;
            groups: string[];
            teams: string[];
        };
        next: {
            name: string;
            phone: string | null;
            role: string;
            alertNotificationType: string;
            sendInvite: boolean;
            groups: string[];
            teams: string[];
        };
        changedFields: {
            name: boolean;
            phone: boolean;
            role: boolean;
            alertNotificationType: boolean;
            sendInvite: boolean;
            groups: boolean;
            teams: boolean;
        };
        groupsDiff: {
            added: string[];
            removed: string[];
        };
        teamsDiff: {
            added: string[];
            removed: string[];
        };
    };
};

type PreviewSummary = {
    rowsReceived: number;
    usersToCreate: number;
    usersToUpdate: number;
    noopUsers: number;
    invalidUsers: number;
    groupsToCreate: number;
    teamsToCreate: number;
    invitesToSend: number;
    blockingErrors: number;
};

type PreviewInvalidUser = {
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    role: string | null;
    alertNotificationType: string | null;
    sendInvite: boolean;
    sourceRowNumbers: number[];
    groupReferences: Array<{
        groupName: string;
        sourceRowNumbers: number[];
    }>;
    teamReferences: Array<{
        groupName: string | null;
        teamName: string;
        sourceRowNumbers: number[];
    }>;
    errors: Array<{
        code: string;
        message: string;
        fields: InvalidUserFieldKey[];
        rowNumbers: number[];
    }>;
};

type PreviewResult = {
    previewId: string;
    fileName: string;
    expiresAt: string;
    users: PreviewUser[];
    invalidUsers?: PreviewInvalidUser[];
    hasBlockingErrors: boolean;
    creationPlan?: {
        groups: Array<{
            name: string;
            isNew: boolean;
            sourceRowNumbers: number[];
            groupLeaders: Array<{
                email: string;
                firstName: string;
                lastName: string;
                role: string;
            }>;
            teams: Array<{
                name: string;
                isNew: boolean;
                sourceRowNumbers: number[];
                teamLeaders: Array<{
                    email: string;
                    firstName: string;
                    lastName: string;
                    role: string;
                }>;
            }>;
        }>;
    };
    summary: PreviewSummary;
    executionState: ExecutionState;
    lastExecuteAttemptAt: string | null;
    executedAt: string | null;
    retryLedger: Record<string, unknown>;
    executionResult: ExecuteResult | null;
};

type PreviewResponse = {
    isValid: boolean;
    message: string;
    result: PreviewResult | null;
};

type InvalidUserIssue = PreviewIssue & {
    highlightedFields: InvalidUserFieldKey[];
};

type InvalidUserEntry = {
    key: string;
    email: string | null;
    rowNumbers: number[];
    firstName: string;
    lastName: string;
    phone: string;
    role: string;
    alertNotificationType: string;
    sendInvite: boolean | null;
    groupName: string | null;
    teamName: string | null;
    issues: InvalidUserIssue[];
};

type SummaryFact = {
    label: string;
    value: string;
};

const STEP_LABELS: Array<{ id: BulkUploadStep; label: string; number: string }> = [
    { id: 'upload', label: 'Upload Workbook', number: '1' },
    { id: 'review', label: 'Review Preview', number: '2' },
    { id: 'results', label: 'Results', number: '3' },
];

const EXECUTION_STATUS_POLL_BASE_DELAY_MS = 3000;
const EXECUTION_STATUS_POLL_MAX_DELAY_MS = 15000;
const EXECUTION_STATUS_POLL_MAX_FAILURES = 5;
const EXECUTION_STATUS_POLL_FAILURE_MESSAGE = 'We could not refresh the bulk upload status. Re-run the preview to check the latest state.';

const summaryCardSx = {
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: '#ffffff',
    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.08)',
};

const naturalCollator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
});

const naturalCompare = (left: string, right: string) => naturalCollator.compare(left, right);

const uniqueNumbers = (numbers: number[]) => Array.from(new Set(numbers)).sort((left, right) => left - right);

const formatRole = (role?: string | null) => {
    if (!role) {
        return 'Unknown';
    }

    return permissionToText[role] || role;
};

const formatBooleanLabel = (value: boolean) => (value ? 'Yes' : 'No');

const formatListLabel = (values: string[]) => (values.length > 0 ? values.join(', ') : 'None');

const formatLeaderNames = (
    leaders: Array<{ firstName: string; lastName: string }>,
) => {
    if (leaders.length === 0) {
        return 'No users';
    }

    return leaders
        .map((leader) => `${leader.firstName} ${leader.lastName}`.trim())
        .join(', ');
};

const formatExpiry = (expiresAt?: string) => {
    if (!expiresAt) {
        return 'Unknown';
    }

    const date = new Date(expiresAt);

    if (Number.isNaN(date.getTime())) {
        return expiresAt;
    }

    return date.toLocaleString();
};

const normalizePreviewErrorMessage = (message: string) => {
    const trimmedMessage = message.trim();
    let parsedMessagePayload: { code?: string; message?: string; name?: string } | null = null;

    if (trimmedMessage.startsWith('{')) {
        try {
            parsedMessagePayload = JSON.parse(trimmedMessage) as { code?: string; message?: string; name?: string };
        } catch {
            parsedMessagePayload = null;
        }
    }

    const normalized = [
        message,
        parsedMessagePayload?.name,
        parsedMessagePayload?.code,
        parsedMessagePayload?.message,
    ]
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
        .join(' ')
        .toLowerCase();

    if (normalized.includes('limit_file_size') || normalized.includes('file too large')) {
        return workbookFileTooLargeMessage();
    }

    if (normalized.includes('exactly one worksheet')) {
        return 'Use the latest template and fill out the System Users sheet. Helper sheets like Notes and Allowed Values are okay.';
    }

    return message;
};

const describeAssignments = (user: PreviewUser) => {
    const groups = user.directGroupAssignments.map((assignment) => assignment.name);
    const groupedTeams = new Map<string, string[]>();

    user.teamAssignments.forEach((assignment) => {
        const existing = groupedTeams.get(assignment.groupName) || [];
        existing.push(assignment.teamName);
        groupedTeams.set(assignment.groupName, existing);
    });

    const teams = Array.from(groupedTeams.entries()).map(([groupName, teamNames]) => (
        `${groupName} - ${teamNames.join(', ')}`
    ));

    return {
        groups: groups.length > 0 ? groups.join(', ') : 'None',
        teams: teams.length > 0 ? teams.join(', ') : 'None',
    };
};

const describeInvalidAssignments = (invalidUser: PreviewInvalidUser) => {
    const groups = invalidUser.groupReferences.map((reference) => reference.groupName);
    const groupedTeams = new Map<string, string[]>();

    invalidUser.teamReferences.forEach((reference) => {
        const groupName = reference.groupName || 'No Group';
        const existing = groupedTeams.get(groupName) || [];
        existing.push(reference.teamName);
        groupedTeams.set(groupName, existing);
    });

    const teams = Array.from(groupedTeams.entries()).map(([groupName, teamNames]) => (
        groupName === 'No Group'
            ? teamNames.join(', ')
            : `${groupName} - ${teamNames.join(', ')}`
    ));

    return {
        groups: groups.length > 0 ? groups.join(', ') : 'None',
        teams: teams.length > 0 ? teams.join(', ') : 'None',
    };
};

const invalidUserFieldLabel: Record<InvalidUserFieldKey, string> = {
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    role: 'Role',
    alertNotificationType: 'Alert',
    groupName: 'Group',
    teamName: 'Team',
    sendInvite: 'Send Invite',
};

const SummaryFactsInline: React.FC<{
    facts: SummaryFact[];
}> = ({
    facts,
}) => (
    <Grid container spacing={1.5}>
        {facts.map((fact) => (
            <Grid item xs={6} sm={3} key={`${fact.label}-${fact.value}`}>
                <Box>
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            mb: 0.2,
                            color: 'text.secondary',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            textTransform: 'uppercase',
                        }}
                    >
                        {fact.label}
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                        {fact.value}
                    </Typography>
                </Box>
            </Grid>
        ))}
    </Grid>
);

const SectionEyebrow: React.FC<{
    color?: string;
    align?: 'left' | 'center' | 'right';
    children: React.ReactNode;
}> = ({
    color = 'text.secondary',
    align = 'left',
    children,
}) => (
    <Typography
        variant="caption"
        sx={{
            display: 'block',
            mb: 0.55,
            color,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            textAlign: align,
            fontSize: '0.68rem',
        }}
    >
        {children}
    </Typography>
);

const metadataLabelSx = {
    display: 'block',
    mb: 0.35,
    color: 'text.secondary',
    fontWeight: 700,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
};

const metadataChipSx = {
    fontWeight: 700,
    height: 'auto',
    alignSelf: 'flex-start',
    maxWidth: '100%',
    '& .MuiChip-label': {
        px: 1.35,
        py: 0.55,
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
        display: 'block',
        lineHeight: 1.2,
    },
};

const invalidUsersTableColumns = {
    xs: '1fr',
    md: '2.2fr 0.9fr 1.6fr 1fr 2fr',
};

const newUsersTableColumns = {
    xs: '1fr',
    md: '2.2fr 0.9fr 1.2fr 1.2fr 1.4fr 0.8fr 0.7fr',
};

const updatedUsersTableColumns = {
    xs: '1fr',
    md: '2.2fr 0.9fr 1.2fr 1.2fr 1.4fr 0.7fr',
};

const noopUsersTableColumns = {
    xs: '1fr',
    md: '2.2fr 0.9fr 1.2fr 1.2fr 1.4fr 0.8fr 0.7fr',
};

const VIRTUALIZED_REVIEW_ROWS_THRESHOLD = 30;
const VIRTUALIZED_REVIEW_ROWS_MAX_HEIGHT = 640;
const VIRTUALIZED_REVIEW_ROWS_OVERSCAN = 5;
const VIRTUALIZED_NEW_USER_ROW_HEIGHT = 62;
const VIRTUALIZED_NOOP_USER_ROW_HEIGHT = 62;
const VIRTUALIZED_UPDATED_USER_ROW_HEIGHT = 72;
const MAX_WORKBOOK_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_WORKBOOK_FILE_SIZE_MB = Math.round(MAX_WORKBOOK_FILE_SIZE_BYTES / (1024 * 1024));

const previewUserRowKey = (user: PreviewUser, index: number) => (
    `${user.email}-${user.sourceRowNumbers.join('-') || 'no-rows'}-${index}`
);

const workbookFileTooLargeMessage = (fileSizeBytes?: number) => {
    if (!fileSizeBytes) {
        return `Please upload a workbook that is ${MAX_WORKBOOK_FILE_SIZE_MB} MB or smaller.`;
    }

    return `Please upload a workbook that is ${MAX_WORKBOOK_FILE_SIZE_MB} MB or smaller. Selected file is ${(fileSizeBytes / (1024 * 1024)).toFixed(1)} MB.`;
};

type VirtualizedReviewRowRenderProps<T> = {
    item: T;
    index: number;
    style: React.CSSProperties;
    measureRef: React.Ref<HTMLDivElement>;
};

type VirtualizedReviewListProps<T> = {
    items: T[];
    defaultItemSize: number;
    getItemKey: (item: T, index: number) => React.Key;
    renderItem: (props: VirtualizedReviewRowRenderProps<T>) => React.ReactNode;
};

type MeasuredVirtualizedReviewRowProps<T> = {
    item: T;
    index: number;
    style: React.CSSProperties;
    renderItem: (props: VirtualizedReviewRowRenderProps<T>) => React.ReactNode;
    onSizeChange: (index: number, size: number) => void;
};

function MeasuredVirtualizedReviewRow<T>({
    item,
    index,
    style,
    renderItem,
    onSizeChange,
}: MeasuredVirtualizedReviewRowProps<T>) {
    const measureRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const element = measureRef.current;

        if (!element) {
            return;
        }

        const measure = () => {
            onSizeChange(index, element.getBoundingClientRect().height);
        };

        measure();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(() => {
            measure();
        });

        observer.observe(element);

        return () => {
            observer.disconnect();
        };
    }, [index, item, onSizeChange]);

    return <>{renderItem({ item, index, style, measureRef })}</>;
}

function VirtualizedReviewList<T>({
    items,
    defaultItemSize,
    getItemKey,
    renderItem,
}: VirtualizedReviewListProps<T>) {
    const listRef = useRef<VariableSizeList>(null);
    const sizeMapRef = useRef<Record<number, number>>({});
    const [, setMeasurementVersion] = useState(0);

    useEffect(() => {
        sizeMapRef.current = {};
        listRef.current?.resetAfterIndex(0, true);
        setMeasurementVersion((version) => version + 1);
    }, [items]);

    const setSize = useCallback((index: number, size: number) => {
        const nextSize = Math.max(1, Math.ceil(size));

        if (sizeMapRef.current[index] === nextSize) {
            return;
        }

        sizeMapRef.current[index] = nextSize;
        listRef.current?.resetAfterIndex(index, true);
        setMeasurementVersion((version) => version + 1);
    }, []);

    const getItemSize = useCallback(
        (index: number) => sizeMapRef.current[index] ?? defaultItemSize,
        [defaultItemSize],
    );

    const totalHeight = items.reduce(
        (sum, _, index) => sum + (sizeMapRef.current[index] ?? defaultItemSize),
        0,
    );

    return (
        <VariableSizeList
            ref={listRef}
            width="100%"
            height={Math.min(VIRTUALIZED_REVIEW_ROWS_MAX_HEIGHT, Math.max(defaultItemSize, totalHeight))}
            itemCount={items.length}
            itemSize={getItemSize}
            estimatedItemSize={defaultItemSize}
            itemKey={(index) => getItemKey(items[index], index)}
            overscanCount={VIRTUALIZED_REVIEW_ROWS_OVERSCAN}
        >
            {({ index, style }) => (
                <MeasuredVirtualizedReviewRow
                    item={items[index]}
                    index={index}
                    style={style}
                    renderItem={renderItem}
                    onSizeChange={setSize}
                />
            )}
        </VariableSizeList>
    );
}

const invalidUserAlertLabel = (user: InvalidUserEntry) => {
    if (user.issues.length > 1) {
        return 'Multiple Conflicts';
    }

    const issue = user.issues[0];

    switch (issue?.code) {
        case 'INVALID_ALERT_NOTIFICATION_TYPE':
            return 'Invalid Alert Notification Type';
        case 'INVALID_ROLE':
            return 'Invalid Role';
        case 'DUPLICATE_EMAIL_MISMATCH':
            return 'Duplicate Email Mismatch';
        case 'EMAIL_BELONGS_TO_OTHER_CUSTOMER':
            return 'Email Belongs To Other Customer';
        default:
            return issue?.message || 'Validation Issue(s)';
    }
};

const issueIconSx = {
    color: '#c0352b',
    fontSize: 18,
};

const issueIconForCode = (code?: string) => {
    switch (code) {
        case 'MISSING_REQUIRED_FIELDS':
            return <PlaylistRemoveRoundedIcon sx={issueIconSx} />;
        case 'INVALID_ROLE':
            return <GppBadRoundedIcon sx={issueIconSx} />;
        case 'INVALID_ALERT_NOTIFICATION_TYPE':
            return <NotificationsOffRoundedIcon sx={issueIconSx} />;
        case 'TEAM_WITHOUT_GROUP':
            return <AccountTreeRoundedIcon sx={issueIconSx} />;
        case 'VIEW_ONLY_GROUP_ASSIGNMENT_NOT_ALLOWED':
        case 'VIEW_ONLY_TEAM_ASSIGNMENT_NOT_ALLOWED':
            return <VisibilityOffRoundedIcon sx={issueIconSx} />;
        case 'TEAM_LEADER_GROUP_ONLY_ASSIGNMENT_NOT_ALLOWED':
            return <AccountTreeRoundedIcon sx={issueIconSx} />;
        case 'DUPLICATE_EMAIL_MISMATCH':
            return <ContentCopyRoundedIcon sx={issueIconSx} />;
        case 'EMAIL_BELONGS_TO_OTHER_CUSTOMER':
            return <BusinessRoundedIcon sx={issueIconSx} />;
        default:
            return <ErrorOutlineRoundedIcon sx={issueIconSx} />;
    }
};

const InvalidUserAccordion: React.FC<{
    user: InvalidUserEntry;
    primaryMain: string;
    primaryLight: string;
}> = ({
    user,
    primaryMain,
    primaryLight,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [hoveredIssueIndex, setHoveredIssueIndex] = useState<number | null>(null);
    const highlightedFields = hoveredIssueIndex === null
        ? [] as InvalidUserFieldKey[]
        : user.issues[hoveredIssueIndex]?.highlightedFields || [];

    const fieldValue = (field: InvalidUserFieldKey) => {
        switch (field) {
            case 'name':
                return `${user.firstName} ${user.lastName}`.trim();
            case 'email':
                return user.email || 'Unknown';
            case 'phone':
                return user.phone || 'Unknown';
            case 'role':
                return formatRole(user.role);
            case 'alertNotificationType':
                return user.alertNotificationType || 'Unknown';
            case 'groupName':
                return user.groupName || 'None';
            case 'teamName':
                return user.teamName || 'None';
            case 'sendInvite':
                return user.sendInvite === null ? 'Unknown' : user.sendInvite ? 'Yes' : 'No';
            default:
                return '';
        }
    };

    const groupTeamLabel = `${fieldValue('groupName')} / ${fieldValue('teamName')}`;
    const validationLabel = invalidUserAlertLabel(user);
    const roleIsProblematic = user.issues.some((issue) => issue.highlightedFields.includes('role'));
    const primaryIssueCode = user.issues.length === 1 ? user.issues[0]?.code : undefined;

const detailField = (
    label: string,
    value: string,
    highlighted: boolean,
    italic = false,
    ) => (
        <Box
            sx={{
                p: 1.05,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: highlighted ? alpha('#ef4444', 0.32) : 'transparent',
                backgroundColor: highlighted ? alpha('#ef4444', 0.08) : alpha('#ffffff', 0.6),
                boxShadow: highlighted ? '0 0 0 1px rgba(239,68,68,0.08)' : 'none',
                transition: 'all 0.15s ease',
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    display: 'block',
                    mb: 0.28,
                    color: highlighted ? '#b91c1c' : 'text.secondary',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    fontSize: '0.68rem',
                }}
            >
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    color: highlighted ? '#991b1b' : 'text.primary',
                    fontWeight: highlighted ? 700 : 400,
                    fontStyle: italic ? 'italic' : 'normal',
                    lineHeight: 1.2,
                    fontSize: '0.88rem',
                }}
            >
                {value}
            </Typography>
        </Box>
    );

    return (
        <Accordion
            disableGutters
            expanded={expanded}
            onChange={(_, isExpanded) => {
                setExpanded(isExpanded);
                if (!isExpanded) {
                    setHoveredIssueIndex(null);
                }
            }}
            sx={{
                overflow: 'hidden',
                borderRadius: 0,
                border: 'none',
                borderBottom: '1px solid',
                borderBottomColor: alpha('#0f172a', 0.08),
                boxShadow: 'none',
                backgroundColor: '#fff',
                '&:last-of-type': { borderBottom: 'none' },
                '&:before': {
                    display: 'none',
                },
            }}
        >
            <AccordionSummary
                sx={{
                    px: 0,
                    py: 0,
                    minHeight: 0,
                    '& .MuiAccordionSummary-content': { my: 0 },
                    '& .MuiAccordionSummary-expandIconWrapper': {
                        display: 'none',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: invalidUsersTableColumns,
                        columnGap: 2,
                        alignItems: 'center',
                        width: '100%',
                        px: 2.2,
                        py: 1.2,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Box>
                            <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.15, mb: 0.1, fontSize: '0.88rem' }}>
                                {fieldValue('name')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.15, fontSize: '0.8rem' }}>
                                {fieldValue('email')}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Chip
                                size="small"
                                label={fieldValue('role')}
                                sx={{
                                    fontWeight: 700,
                                    borderRadius: 1,
                                    bgcolor: roleIsProblematic ? alpha('#ef4444', 0.12) : alpha('#0f172a', 0.06),
                                    color: roleIsProblematic ? '#b91c1c' : '#4b5563',
                                    '& .MuiChip-label': {
                                        px: 1.1,
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: highlightedFields.includes('groupName') || highlightedFields.includes('teamName') ? '#991b1b' : 'text.secondary',
                                    fontWeight: highlightedFields.includes('groupName') || highlightedFields.includes('teamName') ? 700 : 400,
                                    fontSize: '0.82rem',
                                }}
                            >
                                {groupTeamLabel}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 400,
                                    fontSize: '0.82rem',
                                }}
                            >
                                {user.rowNumbers.join(', ')}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Stack
                            direction={{ xs: 'row', md: 'row' }}
                            justifyContent="flex-start"
                            alignItems="center"
                            spacing={1.5}
                            sx={{ width: '100%' }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center">
                                {user.issues.length > 1
                                    ? <WarningAmberRoundedIcon sx={{ color: '#c0352b', fontSize: 18 }} />
                                    : issueIconForCode(primaryIssueCode)}
                                <Typography variant="body2" sx={{ color: '#c0352b', fontWeight: 700, lineHeight: 1.15, fontSize: '0.8rem' }}>
                                    {validationLabel}
                                </Typography>
                            </Stack>
                            <Box
                                sx={{
                                    display: 'grid',
                                    placeItems: 'center',
                                    color: 'text.secondary',
                                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                }}
                            >
                                <ExpandMoreRoundedIcon />
                            </Box>
                        </Stack>
                    </Box>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 0, px: 0 }}>
                <Box
                    sx={{
                        borderRadius: 0,
                        border: '1px solid',
                        borderColor: alpha('#ef4444', 0.18),
                        borderLeft: 'none',
                        borderRight: 'none',
                        borderBottom: 'none',
                        backgroundColor: '#fff',
                        px: { xs: 2.2, md: 2.2 },
                        py: { xs: 1.35, md: 1.45 },
                    }}
                >
                    <Grid container spacing={1.8}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.9, color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                User Details
                            </Typography>
                            <Grid container spacing={1.1}>
                                <Grid item xs={6}>
                                    {detailField('Phone', user.phone === 'Unknown' ? 'null' : user.phone, highlightedFields.includes('phone'), user.phone === 'Unknown')}
                                </Grid>
                                <Grid item xs={6}>
                                    {detailField('Invite', user.sendInvite ? 'Send Immediately' : 'Do Not Send', highlightedFields.includes('sendInvite'))}
                                </Grid>
                                <Grid item xs={6}>
                                    {detailField('Role', fieldValue('role'), highlightedFields.includes('role'))}
                                </Grid>
                                <Grid item xs={6}>
                                    {detailField('Alert', fieldValue('alertNotificationType'), highlightedFields.includes('alertNotificationType'))}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.9, color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                Structural Context
                            </Typography>
                            <Grid container spacing={1.1}>
                                <Grid item xs={12} sm={6} md={12}>
                                    {detailField('Group', fieldValue('groupName'), highlightedFields.includes('groupName'), fieldValue('groupName') === 'None')}
                                </Grid>
                                <Grid item xs={12} sm={6} md={12}>
                                    {detailField('Team', fieldValue('teamName'), highlightedFields.includes('teamName'), false)}
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.9, color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                Resolution Required
                            </Typography>
                            <Stack spacing={0.75}>
                        {user.issues.map((issue, index) => (
                            <Box
                                key={`${user.key}-${issue.code}-${issue.rowNumbers.join('-')}`}
                                onMouseEnter={() => setHoveredIssueIndex(index)}
                                onMouseLeave={() => setHoveredIssueIndex(null)}
                                sx={{
                                    display: 'flex',
                                    gap: 1,
                                    alignItems: 'flex-start',
                                    p: 0,
                                    transition: 'all 0.15s ease',
                                    cursor: 'default',
                                }}
                                >
                                {issueIconForCode(issue.code)}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: hoveredIssueIndex === index ? '#991b1b' : '#c0352b',
                                        lineHeight: 1.18,
                                        fontWeight: 400,
                                        fontSize: '0.82rem',
                                    }}
                                >
                                    {issue.message}
                                </Typography>
                            </Box>
                        ))}
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

const NoopUserRow: React.FC<{
    user: PreviewUser;
    style?: React.CSSProperties;
    measureRef?: React.Ref<HTMLDivElement>;
}> = ({
    user,
    style,
    measureRef,
}) => {
    const assignments = describeAssignments(user);
    const alreadyInvited = user.updateDiff?.current?.sendInvite ?? user.sendInvite;

    return (
        <Box
            ref={measureRef}
            style={style}
            sx={{
                display: 'grid',
                gridTemplateColumns: noopUsersTableColumns,
                columnGap: 2,
                alignItems: 'center',
                width: '100%',
                px: 2.2,
                py: 1.2,
                borderBottom: '1px solid',
                borderBottomColor: alpha('#0f172a', 0.08),
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.15, mb: 0.1, fontSize: '0.88rem' }}>
                    {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.15, fontSize: '0.8rem' }}>
                    {user.email}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                <Chip
                    size="small"
                    label={formatRole(user.role)}
                    sx={{
                        fontWeight: 700,
                        borderRadius: 1,
                        bgcolor: alpha('#0f172a', 0.06),
                        color: '#4b5563',
                        '& .MuiChip-label': { px: 1.1 },
                    }}
                />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '0.82rem', color: 'text.secondary' }}>
                    {user.alertNotificationType || 'None'}
                </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '0.82rem', color: 'text.secondary' }}>
                    {assignments.groups}
                </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '0.82rem', color: 'text.secondary' }}>
                    {assignments.teams}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                <Chip
                    size="small"
                    label={alreadyInvited ? 'Yes' : 'No'}
                    sx={{
                        fontWeight: 700,
                        borderRadius: 1,
                        bgcolor: alreadyInvited ? alpha('#059669', 0.1) : alpha('#0f172a', 0.06),
                        color: alreadyInvited ? '#059669' : '#4b5563',
                        '& .MuiChip-label': { px: 1.1 },
                    }}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '0.82rem' }}>
                    {user.sourceRowNumbers.join(', ')}
                </Typography>
            </Box>
        </Box>
    );
};

const NewUserRow: React.FC<{
    user: PreviewUser;
    primaryMain: string;
    style?: React.CSSProperties;
    measureRef?: React.Ref<HTMLDivElement>;
}> = ({
    user,
    primaryMain,
    style,
    measureRef,
}) => {
    const assignments = describeAssignments(user);

    return (
        <Box
            ref={measureRef}
            style={style}
            sx={{
                display: 'grid',
                gridTemplateColumns: newUsersTableColumns,
                columnGap: 2,
                alignItems: 'center',
                width: '100%',
                px: 2.2,
                py: 1.2,
                borderBottom: '1px solid',
                borderBottomColor: alpha('#0f172a', 0.08),
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.15, mb: 0.1, fontSize: '0.88rem' }}>
                    {user.firstName} {user.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.15, fontSize: '0.8rem' }}>
                    {user.email} · {user.phone || 'Unknown'}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', minWidth: 0 }}>
                <Chip
                    size="small"
                    label={formatRole(user.role)}
                    sx={{
                        fontWeight: 700,
                        borderRadius: 1,
                        bgcolor: alpha('#0f172a', 0.06),
                        color: '#4b5563',
                        '& .MuiChip-label': { px: 1.1 },
                    }}
                />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '0.82rem', color: 'text.secondary' }}>
                    {user.alertNotificationType}
                </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '0.82rem', color: 'text.secondary' }}>
                    {assignments.groups}
                </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '0.82rem', color: 'text.secondary' }}>
                    {assignments.teams}
                </Typography>
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Chip
                    size="small"
                    label={user.sendInvite ? 'Yes' : 'No'}
                    sx={{
                        fontWeight: 700,
                        borderRadius: 1,
                        bgcolor: alpha('#0f172a', 0.06),
                        color: '#4b5563',
                        '& .MuiChip-label': { px: 1.1 },
                    }}
                />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '0.82rem' }}>
                    {user.sourceRowNumbers.join(', ')}
                </Typography>
            </Box>
        </Box>
    );
};

const UpdateValueCell: React.FC<{
    currentValue: string;
    nextValue: string;
    changed?: boolean;
    extra?: React.ReactNode;
}> = ({ currentValue, nextValue, changed, extra }) => (
    <Box sx={{ minWidth: 0 }}>
        {changed ? (
            <>
                <Typography variant="body2" sx={{ lineHeight: 1.25, fontSize: '0.82rem' }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{currentValue}</Box>
                    <Box component="span" sx={{ mx: 0.5, color: '#b45309', fontWeight: 700 }}>→</Box>
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{nextValue}</Box>
                </Typography>
                {extra}
            </>
        ) : (
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.25, fontSize: '0.82rem' }}>
                {nextValue || 'None'}
            </Typography>
        )}
    </Box>
);

const updateDetailField = (
    label: string,
    currentValue: string,
    nextValue: string,
    changed?: boolean,
    extra?: React.ReactNode,
) => (
    <Box
        sx={{
            p: 1.05,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: changed ? alpha('#b45309', 0.25) : 'transparent',
            backgroundColor: changed ? alpha('#b45309', 0.06) : alpha('#f8fafc', 0.8),
        }}
    >
        <Typography
            variant="caption"
            sx={{
                display: 'block',
                mb: 0.28,
                color: changed ? '#b45309' : 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                fontSize: '0.68rem',
            }}
        >
            {label}
        </Typography>
        {changed ? (
            <>
                <Typography variant="body2" sx={{ lineHeight: 1.2, fontSize: '0.88rem' }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>{currentValue}</Box>
                    <Box component="span" sx={{ mx: 0.5, color: '#b45309', fontWeight: 700 }}>→</Box>
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{nextValue}</Box>
                </Typography>
                {extra}
            </>
        ) : (
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 400, lineHeight: 1.2, fontSize: '0.88rem' }}>
                {nextValue || 'None'}
            </Typography>
        )}
    </Box>
);

const UpdateUserRow: React.FC<{
    user: PreviewUser;
    style?: React.CSSProperties;
    measureRef?: React.Ref<HTMLDivElement>;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
}> = ({
    user,
    style,
    measureRef,
    expanded: expandedProp,
    onExpandedChange,
}) => {
    const [localExpanded, setLocalExpanded] = useState(false);
    const updateDiff = user.updateDiff;
    const changedFields = updateDiff?.changedFields;
    const expanded = expandedProp ?? localExpanded;

    const hiddenChanges = [
        changedFields?.phone && 'Phone',
        changedFields?.sendInvite && 'Invite',
        changedFields?.name && 'Name',
    ].filter(Boolean) as string[];

    const handleExpandedChange = (isExpanded: boolean) => {
        if (onExpandedChange) {
            onExpandedChange(isExpanded);
            return;
        }

        setLocalExpanded(isExpanded);
    };

    return (
        <Accordion
            ref={measureRef}
            style={style}
            disableGutters
            expanded={expanded}
            onChange={(_, isExpanded) => handleExpandedChange(isExpanded)}
            sx={{
                overflow: 'hidden',
                borderRadius: 0,
                border: 'none',
                borderBottom: '1px solid',
                borderBottomColor: alpha('#0f172a', 0.08),
                boxShadow: 'none',
                backgroundColor: '#fff',
                '&:before': { display: 'none' },
            }}
        >
            <AccordionSummary
                sx={{
                    px: 0,
                    py: 0,
                    minHeight: 0,
                    '& .MuiAccordionSummary-content': { my: 0 },
                    '& .MuiAccordionSummary-expandIconWrapper': { display: 'none' },
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: updatedUsersTableColumns,
                        columnGap: 2,
                        alignItems: 'center',
                        width: '100%',
                        px: 2.2,
                        py: 1.2,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body1" fontWeight={700} sx={{ lineHeight: 1.15, mb: 0.1, fontSize: '0.88rem' }}>
                            {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.15, fontSize: '0.8rem' }}>
                            {user.email}
                        </Typography>
                    </Box>
                    <UpdateValueCell
                        currentValue={updateDiff ? formatRole(updateDiff.current.role) : formatRole(user.role)}
                        nextValue={updateDiff ? formatRole(updateDiff.next.role) : formatRole(user.role)}
                        changed={changedFields?.role}
                    />
                    <UpdateValueCell
                        currentValue={updateDiff?.current.alertNotificationType || 'None'}
                        nextValue={updateDiff?.next.alertNotificationType || user.alertNotificationType || 'None'}
                        changed={changedFields?.alertNotificationType}
                    />
                    <UpdateValueCell
                        currentValue={updateDiff ? formatListLabel(updateDiff.current.groups) : 'None'}
                        nextValue={updateDiff ? formatListLabel(updateDiff.next.groups) : 'None'}
                        changed={changedFields?.groups}
                    />
                    <UpdateValueCell
                        currentValue={updateDiff ? formatListLabel(updateDiff.current.teams) : 'None'}
                        nextValue={updateDiff ? formatListLabel(updateDiff.next.teams) : 'None'}
                        changed={changedFields?.teams}
                    />
                    <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                        {hiddenChanges.length > 0 && (
                            <Chip
                                size="small"
                                label={`+${hiddenChanges.length} more`}
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.65rem',
                                    height: 20,
                                    bgcolor: alpha('#b45309', 0.1),
                                    color: '#b45309',
                                    '& .MuiChip-label': { px: 0.8 },
                                }}
                            />
                        )}
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: '0.82rem' }}>
                            {user.sourceRowNumbers.join(', ')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                placeItems: 'center',
                                color: 'text.secondary',
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                            }}
                        >
                            <ExpandMoreRoundedIcon sx={{ fontSize: 20 }} />
                        </Box>
                    </Stack>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 0, px: 0 }}>
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: alpha('#b45309', 0.15),
                        backgroundColor: '#fff',
                        px: 2.2,
                        py: 1.45,
                    }}
                >
                    <Grid container spacing={1.1}>
                        <Grid item xs={6} sm={4} md={2}>
                            {updateDetailField(
                                'Name',
                                updateDiff?.current.name || 'None',
                                updateDiff?.next.name || `${user.firstName} ${user.lastName}`,
                                changedFields?.name,
                            )}
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            {updateDetailField(
                                'Phone',
                                updateDiff?.current.phone || 'None',
                                updateDiff?.next.phone || user.phone || 'None',
                                changedFields?.phone,
                            )}
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            {updateDetailField(
                                'Role',
                                updateDiff ? formatRole(updateDiff.current.role) : formatRole(user.role),
                                updateDiff ? formatRole(updateDiff.next.role) : formatRole(user.role),
                                changedFields?.role,
                            )}
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            {updateDetailField(
                                'Alert',
                                updateDiff?.current.alertNotificationType || 'None',
                                updateDiff?.next.alertNotificationType || user.alertNotificationType || 'None',
                                changedFields?.alertNotificationType,
                            )}
                        </Grid>
                        <Grid item xs={6} sm={4} md={2}>
                            {updateDetailField(
                                'Send Invite',
                                updateDiff ? formatBooleanLabel(updateDiff.current.sendInvite) : 'No',
                                updateDiff ? formatBooleanLabel(updateDiff.next.sendInvite) : formatBooleanLabel(user.sendInvite),
                                changedFields?.sendInvite,
                            )}
                        </Grid>
                    </Grid>
                    {(changedFields?.groups || changedFields?.teams) && updateDiff && (
                        <Grid container spacing={1.1} mt={0.2}>
                            {changedFields.groups && (
                                <Grid item xs={12} sm={6}>
                                    {updateDetailField(
                                        'Groups',
                                        formatListLabel(updateDiff.current.groups),
                                        formatListLabel(updateDiff.next.groups),
                                        true,
                                        <Stack spacing={0} mt={0.3}>
                                            {updateDiff.groupsDiff.added.length > 0 && (
                                                <Typography variant="caption" color="#166534" sx={{ fontSize: '0.72rem' }}>
                                                    + {updateDiff.groupsDiff.added.join(', ')}
                                                </Typography>
                                            )}
                                            {updateDiff.groupsDiff.removed.length > 0 && (
                                                <Typography variant="caption" color="#b91c1c" sx={{ fontSize: '0.72rem' }}>
                                                    - {updateDiff.groupsDiff.removed.join(', ')}
                                                </Typography>
                                            )}
                                        </Stack>,
                                    )}
                                </Grid>
                            )}
                            {changedFields.teams && (
                                <Grid item xs={12} sm={6}>
                                    {updateDetailField(
                                        'Teams',
                                        formatListLabel(updateDiff.current.teams),
                                        formatListLabel(updateDiff.next.teams),
                                        true,
                                        <Stack spacing={0} mt={0.3}>
                                            {updateDiff.teamsDiff.added.length > 0 && (
                                                <Typography variant="caption" color="#166534" sx={{ fontSize: '0.72rem' }}>
                                                    + {updateDiff.teamsDiff.added.join(', ')}
                                                </Typography>
                                            )}
                                            {updateDiff.teamsDiff.removed.length > 0 && (
                                                <Typography variant="caption" color="#b91c1c" sx={{ fontSize: '0.72rem' }}>
                                                    - {updateDiff.teamsDiff.removed.join(', ')}
                                                </Typography>
                                            )}
                                        </Stack>,
                                    )}
                                </Grid>
                            )}
                        </Grid>
                    )}
                    {user.warnings.length > 0 && (
                        <Box mt={1}>
                            {user.warnings
                                .filter((w) => w !== 'Existing user assignments already match the imported data.')
                                .map((warning) => (
                                    <Typography key={warning} variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                        {warning}
                                    </Typography>
                                ))}
                        </Box>
                    )}
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

const formatExecuteUserDisplayName = (
    user: Pick<ExecuteResultUser, 'email' | 'firstName' | 'lastName'> | Pick<ExecuteFailedUser, 'email' | 'firstName' | 'lastName'>,
) => {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();

    return fullName || user.email.split('@')[0];
};

const ExpandableExecuteError: React.FC<{
    message: string;
    color?: string;
}> = ({
    message,
    color = '#ba1a1a',
}) => {
    const [expanded, setExpanded] = useState(false);
    const isLong = message.length > 200;
    const displayedMessage = isLong && !expanded ? `${message.slice(0, 200)}...` : message;

    return (
        <Box>
            <Typography
                component="code"
                sx={{
                    display: 'block',
                    fontFamily: 'ui-monospace, SFMono-Regular, SFMono-Regular, Menlo, monospace',
                    fontSize: '0.78rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color,
                    bgcolor: alpha(color, 0.06),
                    borderRadius: 2,
                    px: 1.4,
                    py: 1.1,
                }}
            >
                {displayedMessage}
            </Typography>
            {isLong && (
                <Button
                    size="small"
                    onClick={() => setExpanded((current) => !current)}
                    sx={{
                        mt: 0.8,
                        px: 0,
                        minWidth: 0,
                        textTransform: 'none',
                        fontWeight: 700,
                    }}
                >
                    {expanded ? 'Show less' : 'Show full error'}
                </Button>
            )}
        </Box>
    );
};

export const SystemUserBulkUpload: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const customerName = useSelector((state: RootState) => state.customer.details.customer?.name);
    const [searchParams, setSearchParams] = useSearchParams();
    const [step, setStep] = useState<BulkUploadStep>('upload');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedFileName, setSelectedFileName] = useState('');
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [previewData, setPreviewData] = useState<PreviewResult | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isTemplateDownloadLoading, setIsTemplateDownloadLoading] = useState(false);
    const [isCancellingPreview, setIsCancellingPreview] = useState(false);
    const [isExecuting, setIsExecuting] = useState(false);
    const [showAllNewUsers, setShowAllNewUsers] = useState(false);
    const [replaceModalOpen, setReplaceModalOpen] = useState(false);
    const [cancelModalOpen, setCancelModalOpen] = useState(false);
    const [pendingReplacementFile, setPendingReplacementFile] = useState<File | null>(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const [executeConfirmOpen, setExecuteConfirmOpen] = useState(false);
    const [executeResult, setExecuteResult] = useState<ExecuteResult | null>(null);
    const [executeError, setExecuteError] = useState<string | null>(null);
    const [succeededExpanded, setSucceededExpanded] = useState(false);
    const [structureCompletedExpanded, setStructureCompletedExpanded] = useState(false);
    const [executionLeaveDialogOpen, setExecutionLeaveDialogOpen] = useState(false);
    const [expandedUpdatedRows, setExpandedUpdatedRows] = useState<Record<string, boolean>>({});

    const fileInputRef = useRef<HTMLInputElement>(null);
    const lastLoadedPreviewIdRef = useRef<string | null>(null);
    const dragDepthRef = useRef(0);
    const pendingNavigationRef = useRef<string | null>(null);
    const pendingExecutionNavigationRef = useRef<null | (() => void)>(null);
    const executionNavigationBypassRef = useRef(false);

    const primaryMain = theme.palette.primary.main;
    const primaryDark = theme.palette.primary.dark;
    const primaryLight = theme.palette.primary.light;
    const primaryDarker = (theme.palette.primary as any).darker || primaryDark;
    const primaryLighter = (theme.palette.primary as any).lighter || primaryLight;

    const createdUsers = useMemo(
        () => previewData?.users.filter((user) => user.action === 'create') || [],
        [previewData],
    );
    const updatedUsers = useMemo(
        () => previewData?.users.filter((user) => user.action === 'update') || [],
        [previewData],
    );
    const noopUsers = useMemo(
        () => previewData?.users.filter((user) => user.action === 'noop') || [],
        [previewData],
    );
    const activePreviewId = previewData?.previewId || searchParams.get('previewId');
    const mockResultMode = searchParams.get('mockResult');

    useEffect(() => {
        setExpandedUpdatedRows({});
    }, [activePreviewId]);

    // DEV-ONLY: ?mockResult=success or ?mockResult=partial to preview results screens
    useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            return;
        }

        if (mockResultMode !== 'success' && mockResultMode !== 'partial') {
            return;
        }

        setSelectedFileName('system-user-upload.xlsx');

        if (mockResultMode === 'success') {
            setExecuteResult({
                status: 'success',
                failedPhase: null,
                previewId: 'mock-preview-id',
                executionState: 'completed',
                executedAt: new Date().toISOString(),
                counts: { groupsCreated: 7, teamsCreated: 13, usersCreated: 6, usersUpdated: 1, invitesAttempted: 14, invitesSent: 14, inviteFailures: 0, userFailures: 0 },
                createdUsers: [
                    { email: 'john.smith@example.com', firstName: 'John', lastName: 'Smith', userId: '1' },
                    { email: 'sarah.jones@example.com', firstName: 'Sarah', lastName: 'Jones', userId: '2' },
                    { email: 'mike.wilson@example.com', firstName: 'Mike', lastName: 'Wilson', userId: '3' },
                    { email: 'anna.lee@example.com', firstName: 'Anna', lastName: 'Lee', userId: '4' },
                    { email: 'tom.baker@example.com', firstName: 'Tom', lastName: 'Baker', userId: '5' },
                    { email: 'lucy.chen@example.com', firstName: 'Lucy', lastName: 'Chen', userId: '6' },
                ],
                updatedUsers: [
                    { email: 'jane.doe@example.com', firstName: 'Jane', lastName: 'Doe', userId: '7' },
                ],
                inviteResults: [],
                failures: {
                    structure: [],
                    users: [],
                    invites: [],
                },
                nextAction: 'done',
            });
            setStep('results');
        } else if (mockResultMode === 'partial') {
            setExecuteResult({
                status: 'partial',
                failedPhase: 'users',
                previewId: 'mock-preview-id',
                executionState: 'partial_users',
                executedAt: null,
                counts: { groupsCreated: 7, teamsCreated: 13, usersCreated: 130, usersUpdated: 10, invitesAttempted: 140, invitesSent: 130, inviteFailures: 0, userFailures: 10 },
                createdUsers: Array.from({ length: 130 }, (_, i) => ({ email: `user${i}@example.com`, firstName: 'User', lastName: `${i + 1}`, userId: `created-${i}` })),
                updatedUsers: Array.from({ length: 10 }, (_, i) => ({ email: `existing${i}@example.com`, firstName: 'Existing', lastName: `${i + 1}`, userId: `updated-${i}` })),
                inviteResults: [],
                failures: {
                    structure: [],
                    users: [
                        { email: 'm.thorne@enterprise.com', firstName: 'Marcus', lastName: 'Thorne', action: 'create', error: 'Duplicate unique identifier (Employee ID: 4492)' },
                        { email: 'elena.rod@arch-inc.co', firstName: 'Elena', lastName: 'Rodriguez', action: 'create', error: 'Invalid department code: "MK-STRAT-9"' },
                        { email: 'svance@corp.global', firstName: 'Samuel', lastName: 'Vance', action: 'create', error: 'Missing required field: "Work Location"' },
                        { email: 'j.park@newco.io', firstName: 'Jin', lastName: 'Park', action: 'update', error: 'User record locked by concurrent operation' },
                        { email: 'maria.g@startup.dev', firstName: 'Maria', lastName: 'Garcia', action: 'create', error: 'Email domain not in allowlist' },
                        { email: 'alex.k@mega.corp', firstName: 'Alex', lastName: 'Kim', action: 'create', error: 'Phone number format invalid' },
                        { email: 'pat.o@global.net', firstName: 'Pat', lastName: 'OBrien', action: 'create', error: 'Role assignment conflict' },
                        { email: 'li.w@tech.cn', firstName: 'Li', lastName: 'Wang', action: 'create', error: 'Group capacity exceeded' },
                        { email: 'omar.f@defense.gov', firstName: 'Omar', lastName: 'Farouk', action: 'create', error: 'Security clearance required' },
                        { email: 'nina.s@health.org', firstName: 'Nina', lastName: 'Sokolova', action: 'update', error: 'Cannot downgrade role from Admin' },
                    ],
                    invites: [],
                },
                nextAction: 'review_partial_results',
            });
            setStep('results');
        }
    }, [mockResultMode]);

    const applyPreviewResult = React.useCallback((
        nextPreview: PreviewResult,
        nextStep: BulkUploadStep = 'review',
    ) => {
        setPreviewData(nextPreview);
        setSelectedFileName(nextPreview.fileName || '');
        setSelectedFile(null);
        setPendingReplacementFile(null);
        setUploadError(null);
        setShowAllNewUsers(false);
        setExecuteResult(null);
        setExecuteError(null);
        setIsExecuting(false);
        setSucceededExpanded(false);
        setStructureCompletedExpanded(false);
        setStep(nextStep);
        lastLoadedPreviewIdRef.current = nextPreview.previewId;

        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('previewId', nextPreview.previewId);
        setSearchParams(nextParams, { replace: true });

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [searchParams, setSearchParams]);

    const restoreFromPreviewResource = React.useCallback((nextPreview: PreviewResult) => {
        const restoredState = getRestoredBulkUploadState(nextPreview);

        setPreviewData(nextPreview);
        setSelectedFileName(nextPreview.fileName || '');
        setSelectedFile(null);
        setPendingReplacementFile(null);
        setUploadError(null);
        setShowAllNewUsers(false);
        setExecuteError(null);
        setExecuteResult(restoredState.executeResult);
        setIsExecuting(false);
        setSucceededExpanded(false);
        setStructureCompletedExpanded(false);
        setStep(restoredState.step);
        lastLoadedPreviewIdRef.current = nextPreview.previewId;

        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('previewId', nextPreview.previewId);
        setSearchParams(nextParams, { replace: true });

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [searchParams, setSearchParams]);

    const clearPreviewState = React.useCallback((message?: string) => {
        setStep('upload');
        setSelectedFile(null);
        setSelectedFileName('');
        setUploadError(message || null);
        setPreviewData(null);
        setPendingReplacementFile(null);
        setShowAllNewUsers(false);
        setExecuteConfirmOpen(false);
        setExecuteResult(null);
        setExecuteError(null);
        setIsExecuting(false);
        setSucceededExpanded(false);
        setStructureCompletedExpanded(false);
        setExecutionLeaveDialogOpen(false);
        lastLoadedPreviewIdRef.current = null;
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('previewId');
        setSearchParams(nextParams, { replace: true });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [searchParams, setSearchParams]);

    const displayedCreatedUsers = useMemo(
        () => showAllNewUsers ? createdUsers : createdUsers.slice(0, 20),
        [createdUsers, showAllNewUsers],
    );

    const renderReviewUserRows = useCallback((
        users: PreviewUser[],
        defaultItemSize: number,
        renderRow: (user: PreviewUser, index: number, options?: {
            style?: React.CSSProperties;
            measureRef?: React.Ref<HTMLDivElement>;
        }) => React.ReactNode,
    ) => {
        if (users.length <= VIRTUALIZED_REVIEW_ROWS_THRESHOLD) {
            return (
                <Stack spacing={0}>
                    {users.map((user, index) => renderRow(user, index))}
                </Stack>
            );
        }

        return (
            <VirtualizedReviewList
                items={users}
                defaultItemSize={defaultItemSize}
                getItemKey={(user, index) => previewUserRowKey(user, index)}
                renderItem={({ item, index, style, measureRef }) => renderRow(item, index, { style, measureRef })}
            />
        );
    }, []);

    const creationPlanGroups = useMemo(() => {
        const groups = previewData?.creationPlan?.groups || [];

        return groups
            .filter((group) => group.isNew || group.groupLeaders.length > 0 || group.teams.length > 0)
            .sort((left, right) => naturalCompare(left.name, right.name));
    }, [previewData?.creationPlan?.groups]);

    const invalidUsers = useMemo<InvalidUserEntry[]>(() => {
        const source = previewData?.invalidUsers || [];

        return source.map((invalidUser, index) => {
            const assignments = describeInvalidAssignments(invalidUser);
            return {
                key: invalidUser.email || `invalid-${index}-${invalidUser.sourceRowNumbers.join('-')}`,
                email: invalidUser.email,
                rowNumbers: uniqueNumbers(invalidUser.sourceRowNumbers),
                firstName: invalidUser.firstName || 'Unknown',
                lastName: invalidUser.lastName || 'User',
                phone: invalidUser.phone || 'Unknown',
                role: invalidUser.role || 'Unknown',
                alertNotificationType: invalidUser.alertNotificationType || 'Unknown',
                sendInvite: invalidUser.sendInvite,
                groupName: assignments.groups === 'None' ? null : assignments.groups,
                teamName: assignments.teams === 'None' ? null : assignments.teams,
                issues: invalidUser.errors.map((error) => ({
                    ...error,
                    highlightedFields: highlightedFieldsForError(error),
                })),
            };
        }).sort((left, right) => left.rowNumbers[0] - right.rowNumbers[0]);
    }, [previewData?.invalidUsers]);

    const openFilePicker = () => {
        fileInputRef.current?.click();
    };

    const fetchPreviewById = useCallback(async (previewId: string) => {
        const { data } = await api.get<PreviewResponse>(`/customerWebAppUser/bulk-import/preview/${previewId}`);

        if (!data?.isValid || !data?.result) {
            throw new Error(data?.message || 'Preview response was missing result data.');
        }

        return data.result;
    }, []);

    React.useEffect(() => {
        const previewId = searchParams.get('previewId');

        if (!previewId) {
            lastLoadedPreviewIdRef.current = null;
            return;
        }

        if (lastLoadedPreviewIdRef.current === previewId && previewData?.previewId === previewId) {
            return;
        }

        let cancelled = false;

        const loadPreview = async () => {
            setIsPreviewLoading(true);
            try {
                const result = await fetchPreviewById(previewId);

                if (!cancelled) {
                    restoreFromPreviewResource(result);
                }
            } catch (error: any) {
                if (cancelled) {
                    return;
                }

                clearPreviewState('That preview could not be restored. It may have expired, been deleted, or is not accessible.');
                toast.warning(normalizePreviewErrorMessage(handleApiError(error)));
            } finally {
                if (!cancelled) {
                    setIsPreviewLoading(false);
                }
            }
        };

        loadPreview();

        return () => {
            cancelled = true;
        };
    }, [clearPreviewState, fetchPreviewById, previewData?.previewId, restoreFromPreviewResource, searchParams]);

    useBeforeUnload(useCallback((event) => {
        if (step !== 'executing') {
            return;
        }

        event.preventDefault();
        event.returnValue = '';
    }, [step]));

    const requestExecutionNavigation = useCallback((action: () => void) => {
        pendingExecutionNavigationRef.current = action;
        setExecutionLeaveDialogOpen(true);
    }, []);

    useEffect(() => {
        if (step !== 'executing') {
            pendingExecutionNavigationRef.current = null;
            executionNavigationBypassRef.current = false;
            setExecutionLeaveDialogOpen(false);
            return;
        }

        const handleDocumentClick = (event: MouseEvent) => {
            if (executionNavigationBypassRef.current || event.defaultPrevented || event.button !== 0) {
                return;
            }

            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }

            const target = event.target as HTMLElement | null;
            const anchor = target?.closest('a[href]') as HTMLAnchorElement | null;

            if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) {
                return;
            }

            const nextUrl = new URL(anchor.href, window.location.href);
            const currentUrl = new URL(window.location.href);

            if (nextUrl.origin !== currentUrl.origin) {
                return;
            }

            if (nextUrl.pathname === currentUrl.pathname && nextUrl.search === currentUrl.search && nextUrl.hash === currentUrl.hash) {
                return;
            }

            event.preventDefault();
            requestExecutionNavigation(() => {
                navigate(`${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
            });
        };

        const handlePopState = () => {
            if (executionNavigationBypassRef.current) {
                return;
            }

            requestExecutionNavigation(() => {
                window.history.back();
            });

            window.history.go(1);
        };

        document.addEventListener('click', handleDocumentClick, true);
        window.addEventListener('popstate', handlePopState);

        return () => {
            document.removeEventListener('click', handleDocumentClick, true);
            window.removeEventListener('popstate', handlePopState);
        };
    }, [navigate, requestExecutionNavigation, step]);

    useEffect(() => {
        if (step !== 'executing' || isExecuting || !activePreviewId) {
            return;
        }

        let cancelled = false;
        let timeoutId: number | null = null;
        let consecutiveFailures = 0;

        const pollExecutionStatus = async () => {
            try {
                const result = await fetchPreviewById(activePreviewId);

                if (cancelled) {
                    return;
                }

                consecutiveFailures = 0;
                restoreFromPreviewResource(result);

                if (result.executionState === 'in_progress') {
                    timeoutId = window.setTimeout(pollExecutionStatus, EXECUTION_STATUS_POLL_BASE_DELAY_MS);
                }
            } catch (error: any) {
                if (cancelled) {
                    return;
                }

                consecutiveFailures += 1;

                if (consecutiveFailures >= EXECUTION_STATUS_POLL_MAX_FAILURES) {
                    const errorMessage = normalizePreviewErrorMessage(handleApiError(error));

                    setExecuteResult(null);
                    setExecuteError(`${EXECUTION_STATUS_POLL_FAILURE_MESSAGE} ${errorMessage}`.trim());
                    setStep('results');
                    toast.error(EXECUTION_STATUS_POLL_FAILURE_MESSAGE);
                    return;
                }

                const nextDelay = Math.min(
                    EXECUTION_STATUS_POLL_BASE_DELAY_MS * (2 ** (consecutiveFailures - 1)),
                    EXECUTION_STATUS_POLL_MAX_DELAY_MS,
                );

                timeoutId = window.setTimeout(pollExecutionStatus, nextDelay);
            }
        };

        timeoutId = window.setTimeout(pollExecutionStatus, EXECUTION_STATUS_POLL_BASE_DELAY_MS);

        return () => {
            cancelled = true;

            if (timeoutId !== null) {
                window.clearTimeout(timeoutId);
            }
        };
    }, [activePreviewId, fetchPreviewById, isExecuting, restoreFromPreviewResource, step]);

    const clearSelectedFile = () => {
        clearPreviewState();
    };

    const downloadTemplate = async () => {
        let objectUrl: string | null = null;

        setIsTemplateDownloadLoading(true);

        try {
            const response = await fetch(BULK_UPLOAD_TEMPLATE_PATH);

            if (!response.ok) {
                throw new Error(`Template download failed with status ${response.status}.`);
            }

            const contentType = response.headers.get('content-type') || '';
            const arrayBuffer = await response.arrayBuffer();
            const fileBytes = new Uint8Array(arrayBuffer);
            const hasZipSignature = fileBytes.length >= 4
                && fileBytes[0] === 0x50
                && fileBytes[1] === 0x4b
                && (
                    (fileBytes[2] === 0x03 && fileBytes[3] === 0x04)
                    || (fileBytes[2] === 0x05 && fileBytes[3] === 0x06)
                    || (fileBytes[2] === 0x07 && fileBytes[3] === 0x08)
                );

            if (contentType.includes('text/html') || !hasZipSignature) {
                throw new Error('Template download returned HTML instead of an Excel workbook. The current deployment may be missing the template asset.');
            }

            const blob = new Blob(
                [arrayBuffer],
                { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
            );
            const link = document.createElement('a');
            const sanitizedOrgName = (customerName || 'organization')
                .trim()
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '_')
                .replace(/^_+|_+$/g, '');

            objectUrl = URL.createObjectURL(blob);
            link.href = objectUrl;
            link.download = `${sanitizedOrgName || 'organization'}___system_user_workbook.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error: any) {
            const message = error instanceof Error
                ? error.message
                : 'Template download failed. Please try again.';

            toast.error(message);
        } finally {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }

            setIsTemplateDownloadLoading(false);
        }
    };

    const requestNavigationAwayFromPreview = React.useCallback((destination?: string) => {
        if (activePreviewId) {
            pendingNavigationRef.current = destination || null;
            setCancelModalOpen(true);
            return;
        }

        if (destination) {
            navigate(destination);
            return;
        }

        resetFlow();
    }, [activePreviewId, navigate]);

    const resetFlow = () => {
        clearPreviewState();
    };

    const handleIncomingWorkbook = React.useCallback((file: File, source: 'picker' | 'drop') => {
        if (!file) {
            return;
        }

        if (file.size > MAX_WORKBOOK_FILE_SIZE_BYTES) {
            const errorMessage = workbookFileTooLargeMessage(file.size);

            if (previewData?.previewId) {
                toast.error(errorMessage);
            } else {
                setSelectedFile(null);
                setSelectedFileName(file.name);
                setUploadError(errorMessage);
            }

            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            return;
        }

        if (!file.name.toLowerCase().endsWith('.xlsx')) {
            if (previewData?.previewId) {
                toast.error('Please upload an Excel workbook with a .xlsx extension for this preview flow.');
            } else {
                setSelectedFile(null);
                setSelectedFileName(file.name);
                setUploadError('Please upload an Excel workbook with a .xlsx extension for this preview flow.');
            }
            return;
        }

        if (previewData?.previewId) {
            setPendingReplacementFile(file);
            setReplaceModalOpen(true);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            return;
        }

        setPreviewData(null);
        setShowAllNewUsers(false);
        setStep('upload');
        setUploadError(null);
        setSelectedFile(file);
        setSelectedFileName(file.name);
        if (source === 'drop') {
            toast.success(`${file.name} is ready for preview.`);
        }
    }, [previewData?.previewId]);

    const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        handleIncomingWorkbook(file, 'picker');
    };

    React.useEffect(() => {
        const hasFiles = (event: DragEvent) => Array.from(event.dataTransfer?.types || []).includes('Files');

        const handleDragEnter = (event: DragEvent) => {
            if (!hasFiles(event)) {
                return;
            }
            event.preventDefault();
            dragDepthRef.current += 1;
            setIsDragActive(true);
        };

        const handleDragOver = (event: DragEvent) => {
            if (!hasFiles(event)) {
                return;
            }
            event.preventDefault();
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'copy';
            }
            setIsDragActive(true);
        };

        const handleDragLeave = (event: DragEvent) => {
            if (!hasFiles(event)) {
                return;
            }
            event.preventDefault();
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (dragDepthRef.current === 0) {
                setIsDragActive(false);
            }
        };

        const handleDrop = (event: DragEvent) => {
            if (!hasFiles(event)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            dragDepthRef.current = 0;
            setIsDragActive(false);

            const file = event.dataTransfer?.files?.[0];
            if (!file) {
                toast.error('Drop a single .xlsx workbook to continue.');
                return;
            }

            handleIncomingWorkbook(file, 'drop');
        };

        window.addEventListener('dragenter', handleDragEnter);
        window.addEventListener('dragover', handleDragOver);
        window.addEventListener('dragleave', handleDragLeave);
        window.addEventListener('drop', handleDrop);

        return () => {
            window.removeEventListener('dragenter', handleDragEnter);
            window.removeEventListener('dragover', handleDragOver);
            window.removeEventListener('dragleave', handleDragLeave);
            window.removeEventListener('drop', handleDrop);
        };
    }, [handleIncomingWorkbook]);

    const requestPreview = async ({
        file,
        method,
        previewId,
    }: {
        file: File;
        method: 'post' | 'put';
        previewId?: string;
    }) => {
        if (file.size > MAX_WORKBOOK_FILE_SIZE_BYTES) {
            throw new Error(workbookFileTooLargeMessage(file.size));
        }

        const formData = new FormData();
        formData.append('file', file);

        const endpoint = method === 'put' && previewId
            ? `/customerWebAppUser/bulk-import/preview/${previewId}`
            : '/customerWebAppUser/bulk-import/preview';

        const { data } = await api.request<PreviewResponse>({
            url: endpoint,
            method,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });

        if (!data?.isValid || !data?.result) {
            throw new Error(data?.message || 'Preview response was missing result data.');
        }

        return data.result;
    };

    const runPreview = async () => {
        if (!selectedFile) {
            setUploadError('Choose a .xlsx workbook before requesting a preview.');
            return;
        }

        setIsPreviewLoading(true);
        setUploadError(null);

        try {
            const result = await requestPreview({
                file: selectedFile,
                method: 'post',
            });

            applyPreviewResult(result);

            if (result.hasBlockingErrors) {
                toast.warning('Preview completed with blocking validation errors. Review the issues below.');
            } else {
                toast.success('Preview is ready to review.');
            }
        } catch (error: any) {
            const message = normalizePreviewErrorMessage(handleApiError(error));
            setUploadError(message);
            setPreviewData(null);
            setStep('upload');
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const confirmReplacePreview = async () => {
        if (!pendingReplacementFile || !previewData?.previewId) {
            setReplaceModalOpen(false);
            setPendingReplacementFile(null);
            return;
        }

        setIsPreviewLoading(true);
        try {
            const result = await requestPreview({
                file: pendingReplacementFile,
                method: 'put',
                previewId: previewData.previewId,
            });

            applyPreviewResult(result);
            toast.success('Preview replaced successfully.');
            setReplaceModalOpen(false);
            setPendingReplacementFile(null);
        } catch (error: any) {
            toast.error(normalizePreviewErrorMessage(handleApiError(error)));
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const confirmCancelUpload = async () => {
        const previewId = previewData?.previewId || searchParams.get('previewId');

        setIsCancellingPreview(true);
        try {
            if (previewId) {
                await api.delete(`/customerWebAppUser/bulk-import/preview/${previewId}`);
            }

            clearPreviewState();
            setCancelModalOpen(false);
            toast.info('Upload cancelled.');
            if (pendingNavigationRef.current) {
                const destination = pendingNavigationRef.current;
                pendingNavigationRef.current = null;
                navigate(destination);
            }
        } catch (error: any) {
            toast.error(normalizePreviewErrorMessage(handleApiError(error)));
        } finally {
            setIsCancellingPreview(false);
        }
    };

    const runExecute = async () => {
        const previewId = previewData?.previewId;
        if (!previewId) return;

        setStep('executing');
        setIsExecuting(true);
        setExecuteResult(null);
        setExecuteError(null);
        setSucceededExpanded(false);
        setStructureCompletedExpanded(false);
        setPreviewData((current) => current ? {
            ...current,
            executionState: 'in_progress',
            executionResult: null,
        } : current);

        try {
            const { data } = await api.post<ExecuteResponse>(
                `/customerWebAppUser/bulk-import/preview/${previewId}/execute`,
            );

            const executeData = normalizeExecuteResponseResult<ExecuteResult>(data);

            if (executeData) {
                setExecuteResult(executeData);
                if (isBlockedExecuteResult(executeData) && executeData.refreshedPreview) {
                    setPreviewData(executeData.refreshedPreview);
                    setSelectedFileName(executeData.refreshedPreview.fileName || selectedFileName);
                } else {
                    setPreviewData((current) => current ? {
                        ...current,
                        executionState: executeData.executionState,
                        executedAt: executeData.executedAt,
                        executionResult: executeData,
                    } : current);
                }
            } else {
                setExecuteError(data?.message || 'Execute returned no result.');
            }
        } catch (error: any) {
            const blockedExecuteResult = normalizeExecuteResponseResult<ExecuteResult>(error?.response?.data);

            if (blockedExecuteResult) {
                setExecuteResult(blockedExecuteResult);
                if (blockedExecuteResult.refreshedPreview) {
                    setPreviewData(blockedExecuteResult.refreshedPreview);
                    setSelectedFileName(blockedExecuteResult.refreshedPreview.fileName || selectedFileName);
                }
            } else {
                const message = handleApiError(error);
                setExecuteError(message);
            }
        } finally {
            setIsExecuting(false);
            setStep('results');
        }
    };

    const rerunPreviewFromResults = useCallback(async () => {
        const previewId = previewData?.previewId || searchParams.get('previewId');
        if (!previewId) {
            return;
        }

        setIsPreviewLoading(true);
        try {
            const result = await fetchPreviewById(previewId);
            applyPreviewResult(result);
            toast.success('Preview refreshed.');
        } catch (error: any) {
            toast.error(normalizePreviewErrorMessage(handleApiError(error)));
        } finally {
            setIsPreviewLoading(false);
        }
    }, [applyPreviewResult, fetchPreviewById, previewData?.previewId, searchParams]);



    const renderPageHeader = (opts: {
        title: string;
        subtitle?: string;
        showFileName?: boolean;
        breadcrumbs?: Array<{ label: string; onClick?: () => void }>;
        pb?: { xs: number; md: number };
    }) => {
        const currentIndex = STEP_LABELS.findIndex(s => s.id === step);

        return (
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 0,
                    backgroundColor: '#206FA5',
                    color: '#f8fafc',
                    px: { xs: 2.4, md: 4.8 },
                    pt: { xs: 1.7, md: 2.1 },
                    pb: opts.pb || { xs: 2.4, md: 2.8 },
                }}
            >
                <Stack spacing={1.8} sx={{ position: 'relative', zIndex: 1 }}>
                    {/* Breadcrumb */}
                    {opts.breadcrumbs && opts.breadcrumbs.length > 0 && (
                        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap sx={{ color: alpha('#d7e6ff', 0.88) }}>
                            {opts.breadcrumbs.map((crumb, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && <Typography variant="body2" sx={{ color: alpha('#d7e6ff', 0.65) }}>›</Typography>}
                                    {crumb.onClick ? (
                                        <Link
                                            component="button"
                                            type="button"
                                            underline="none"
                                            onClick={crumb.onClick}
                                            sx={{
                                                p: 0, border: 'none', background: 'none',
                                                color: alpha('#d7e6ff', 0.88), fontSize: '0.875rem', fontWeight: 700,
                                                cursor: 'pointer', '&:hover': { color: '#ffffff' },
                                            }}
                                        >
                                            {crumb.label}
                                        </Link>
                                    ) : (
                                        <Typography variant="body2" sx={{ fontWeight: i === opts.breadcrumbs!.length - 1 ? 800 : 700, fontSize: '0.875rem', color: i === opts.breadcrumbs!.length - 1 ? '#ffffff' : alpha('#d7e6ff', 0.88) }}>
                                            {crumb.label}
                                        </Typography>
                                    )}
                                </React.Fragment>
                            ))}
                        </Stack>
                    )}

                    <Stack
                        direction={{ xs: 'column', xl: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', xl: 'flex-end' }}
                        spacing={{ xs: 1.8, xl: 2.6 }}
                    >
                        {/* Title + subtitle/filename */}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 800,
                                    letterSpacing: '-0.05em',
                                    lineHeight: 0.96,
                                    fontSize: { xs: '1.45rem', md: '2rem' },
                                    mb: 0.6,
                                }}
                            >
                                {opts.title}
                            </Typography>
                            {opts.subtitle && (
                                <Typography variant="body2" sx={{ color: alpha('#d7e6ff', 0.82), fontWeight: 600, fontSize: '0.9rem' }}>
                                    {opts.subtitle}
                                </Typography>
                            )}
                            {opts.showFileName && selectedFileName && (
                                <Stack direction="row" spacing={1.1} alignItems="center" sx={{ color: alpha('#d7e6ff', 0.82) }}>
                                    <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                        {selectedFileName}
                                    </Typography>
                                </Stack>
                            )}
                        </Box>

                        {/* Step pills */}
                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.2} alignItems={{ xs: 'stretch', lg: 'center' }}>
                            {STEP_LABELS.map((stepConfig, index) => {
                                const isActive = stepConfig.id === step || (step === 'executing' && stepConfig.id === 'results');
                                const isComplete = currentIndex > index || (step === 'executing' && index < 2);

                                return (
                                    <React.Fragment key={stepConfig.id}>
                                        {index > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 0, lg: 0.1 }, minWidth: { lg: 32 } }}>
                                                <Box sx={{ width: { xs: '100%', lg: 32 }, height: '2px', bgcolor: alpha('#ffffff', 0.5) }} />
                                            </Box>
                                        )}
                                        {isActive ? (
                                            <Box
                                                sx={{
                                                    display: 'flex', alignItems: 'center', gap: 0.85,
                                                    px: 1.55, py: 0.82, borderRadius: 1.2,
                                                    bgcolor: '#fff', color: '#1d4b8d',
                                                    boxShadow: '0 8px 18px rgba(8, 30, 63, 0.12)',
                                                }}
                                            >
                                                <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#1d4b8d', color: '#fff', flexShrink: 0, fontWeight: 800, fontSize: '0.75rem' }}>
                                                    {stepConfig.number}
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', fontSize: '0.82rem' }}>
                                                    {stepConfig.label}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    display: 'flex', alignItems: 'center', gap: 0.85,
                                                    px: 1.55, py: 0.82, borderRadius: 1.2,
                                                    border: '1px solid', borderColor: alpha('#ffffff', 0.14),
                                                    backgroundColor: alpha('#0d3368', 0.26),
                                                }}
                                            >
                                                <Box sx={{ width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: isComplete ? '#fff' : alpha('#325894', 0.65), color: isComplete ? '#1d4b8d' : '#fff', flexShrink: 0, fontWeight: 800, fontSize: '0.75rem' }}>
                                                    {isComplete ? <CheckCircleRoundedIcon sx={{ fontSize: 15 }} /> : stepConfig.number}
                                                </Box>
                                                <Typography variant="body2" sx={{ color: '#f8fafc', fontWeight: 800, letterSpacing: '0.03em', textTransform: 'uppercase', fontSize: '0.82rem' }}>
                                                    {stepConfig.label}
                                                </Typography>
                                            </Box>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </Stack>
                    </Stack>
                </Stack>
            </Box>
        );
    };

    const renderPreviewSummaryBar = (summary: PreviewSummary, issueUserCount: number) => {
        const validUsersCount = summary.usersToCreate + summary.usersToUpdate;

        return (
            <Box
                data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.reviewSummaryCards}
                sx={{
                    display: 'flex',
                    gap: 0.75,
                    overflowX: 'auto',
                }}
            >
                <Card sx={{ ...summaryCardSx, borderRadius: 1.5, boxShadow: '0 8px 24px rgba(15,23,42,0.07)', borderBottom: '2.5px solid', borderBottomColor: '#10386f', flex: 1 }}>
                    <CardContent sx={{ px: 1, py: 1.05, '&:last-child': { pb: 1.05 } }}>
                        <SectionEyebrow>Received</SectionEyebrow>
                        <Stack direction="row" spacing={0.3} alignItems="baseline">
                            <Typography
                                data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.reviewSummaryRows}
                                fontWeight={800}
                                sx={{ lineHeight: 1, fontSize: '1.15rem', color: primaryDark }}
                            >
                                {summary.rowsReceived}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>Rows</Typography>
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={{ ...summaryCardSx, borderRadius: 1.5, boxShadow: '0 8px 24px rgba(15,23,42,0.07)', borderBottom: '2.5px solid', borderBottomColor: '#10386f', flex: 1 }}>
                    <CardContent sx={{ px: 1, py: 1.05, '&:last-child': { pb: 1.05 } }}>
                        <SectionEyebrow>To Create</SectionEyebrow>
                        <Stack direction="row" spacing={0} divider={<Divider orientation="vertical" flexItem sx={{ mx: 0.8, borderColor: alpha('#0f172a', 0.1) }} />}>
                            {[
                                { value: validUsersCount, label: 'Users' },
                                { value: summary.groupsToCreate, label: 'Groups' },
                                { value: summary.teamsToCreate, label: 'Teams' },
                            ].map((m) => (
                                <Stack key={m.label} direction="row" spacing={0.3} alignItems="baseline">
                                    <Typography
                                        data-testid={
                                            m.label === 'Users'
                                                ? SYSTEM_USER_BULK_UPLOAD_TEST_IDS.reviewSummaryUsers
                                                : m.label === 'Groups'
                                                    ? SYSTEM_USER_BULK_UPLOAD_TEST_IDS.reviewSummaryGroups
                                                    : SYSTEM_USER_BULK_UPLOAD_TEST_IDS.reviewSummaryTeams
                                        }
                                        fontWeight={800}
                                        sx={{ color: primaryDark, lineHeight: 1, fontSize: '1.15rem' }}
                                    >
                                        {m.value}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{m.label}</Typography>
                                </Stack>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>

                <Card sx={{ ...summaryCardSx, borderRadius: 1.5, boxShadow: '0 8px 24px rgba(15,23,42,0.07)', borderBottom: '2.5px solid', borderBottomColor: '#10386f', flex: 1 }}>
                    <CardContent sx={{ px: 1, py: 1.05, '&:last-child': { pb: 1.05 } }}>
                        <SectionEyebrow>Invites</SectionEyebrow>
                        <Stack direction="row" spacing={0.3} alignItems="baseline">
                            <Typography
                                data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.reviewSummaryInvites}
                                fontWeight={800}
                                sx={{ lineHeight: 1, fontSize: '1.15rem', color: primaryDark }}
                            >
                                {summary.invitesToSend}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>To Send</Typography>
                        </Stack>
                    </CardContent>
                </Card>

                {(summary.noopUsers > 0 || summary.usersToUpdate > 0) && (
                    <Card sx={{ ...summaryCardSx, borderRadius: 1.5, boxShadow: '0 8px 24px rgba(15,23,42,0.07)', borderBottom: '2.5px solid', borderBottomColor: '#10386f', flex: 1 }}>
                        <CardContent sx={{ px: 1, py: 1.05, '&:last-child': { pb: 1.05 } }}>
                            <SectionEyebrow>Existing</SectionEyebrow>
                            <Stack direction="row" spacing={0} divider={<Divider orientation="vertical" flexItem sx={{ mx: 0.8, borderColor: alpha('#0f172a', 0.1) }} />}>
                                <Stack direction="row" spacing={0.3} alignItems="baseline">
                                    <Typography fontWeight={800} sx={{ color: primaryDark, lineHeight: 1, fontSize: '1.15rem' }}>{summary.usersToUpdate}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>Update</Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.3} alignItems="baseline">
                                    <Typography fontWeight={800} sx={{ color: '#6b7280', lineHeight: 1, fontSize: '1.15rem' }}>{summary.noopUsers}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>Same</Typography>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                )}

                <Card sx={{ borderRadius: 1.5, border: '1px solid', borderColor: alpha('#c62828', 0.18), backgroundColor: '#fff4f2', borderBottom: '2.5px solid', borderBottomColor: '#c62828', boxShadow: '0 8px 24px rgba(186,26,26,0.06)', flex: 1 }}>
                    <CardContent sx={{ px: 1, py: 1.05, display: 'flex', alignItems: 'center', gap: 1, '&:last-child': { pb: 1.05 } }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <SectionEyebrow color="#93000a">Issues</SectionEyebrow>
                            <Stack direction="row" spacing={0.3} alignItems="baseline" sx={{ color: '#ba1a1a' }}>
                                <Typography
                                    data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.reviewSummaryBlockingErrors}
                                    fontWeight={800}
                                    sx={{ lineHeight: 1, fontSize: '1.15rem' }}
                                >
                                    {summary.blockingErrors}
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>in {issueUserCount}</Typography>
                            </Stack>
                        </Box>
                        <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: alpha('#c62828', 0.1), display: 'grid', placeItems: 'center', color: '#c62828', flexShrink: 0 }}>
                            <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        );
    };

    const renderReviewSummarySection = (summary: PreviewSummary) => (
        <Box sx={{ position: 'relative', mb: 0, px: 0 }}>
            {renderPageHeader({
                title: 'Review Import Data',
                showFileName: true,
                breadcrumbs: [
                    { label: 'System Users', onClick: () => requestNavigationAwayFromPreview('/system-users') },
                    { label: 'Upload', onClick: () => requestNavigationAwayFromPreview() },
                    { label: 'Review Preview' },
                ],
                pb: { xs: 4.4, md: 4.8 },
            })}

            <Box
                sx={{
                    mt: { xs: 3, md: 3 },
                    mx: { xs: 2.4, md: 4.8 },
                    position: 'relative',
                    zIndex: 2,
                }}
            >
                {renderPreviewSummaryBar(summary, invalidUsers.length)}
            </Box>
        </Box>
    );

    const renderUpdates = (users: PreviewUser[]) => (
        <Card
            sx={{
                ...summaryCardSx,
                borderColor: alpha('#0f172a', 0.08),
                backgroundColor: '#fff',
            }}
        >
            <CardContent sx={{ p: 0 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1}
                    sx={{ px: 2.2, pt: 2.2, pb: 1.3 }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <InfoOutlinedIcon sx={{ color: '#b45309', fontSize: 20 }} />
                        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.02rem' }}>
                            User update plan ({users.length})
                        </Typography>
                    </Stack>
                </Stack>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: updatedUsersTableColumns,
                        columnGap: 2,
                        px: 2.2,
                        py: 1.1,
                        borderTop: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: alpha('#0f172a', 0.08),
                        backgroundColor: alpha('#f8fafc', 0.8),
                    }}
                >
                    {['Name/Email', 'Role', 'Alert', 'Groups', 'Teams', ''].map((label) => (
                        <Box key={label || 'row'} sx={{ display: 'flex', justifyContent: label ? 'flex-start' : 'flex-end' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.68rem' }}>
                                {label || 'Row(s)'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                {renderReviewUserRows(
                    users,
                    VIRTUALIZED_UPDATED_USER_ROW_HEIGHT,
                    (user, index, options) => {
                        const rowKey = previewUserRowKey(user, index);

                        return (
                            <UpdateUserRow
                                key={rowKey}
                                user={user}
                                style={options?.style}
                                measureRef={options?.measureRef}
                                expanded={expandedUpdatedRows[rowKey] || false}
                                onExpandedChange={(isExpanded) => {
                                    setExpandedUpdatedRows((current) => {
                                        if ((current[rowKey] || false) === isExpanded) {
                                            return current;
                                        }

                                        return {
                                            ...current,
                                            [rowKey]: isExpanded,
                                        };
                                    });
                                }}
                            />
                        );
                    },
                )}
            </CardContent>
        </Card>
    );

    const renderNoopUsers = (users: PreviewUser[]) => (
        <Card
            sx={{
                ...summaryCardSx,
                borderColor: alpha('#0f172a', 0.06),
                backgroundColor: '#fff',
            }}
        >
            <CardContent sx={{ p: 0 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1}
                    sx={{ px: 2.2, pt: 2.2, pb: 1.3 }}
                >
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CheckCircleRoundedIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.02rem' }}>
                            No changes ({users.length})
                        </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 500 }}>
                        These existing users were found in the workbook but have no field changes to apply.
                    </Typography>
                </Stack>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: noopUsersTableColumns,
                        columnGap: 2,
                        px: 2.2,
                        py: 1.1,
                        borderTop: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: alpha('#0f172a', 0.08),
                        backgroundColor: alpha('#f8fafc', 0.8),
                    }}
                >
                    {['Name/Email', 'Role', 'Alert', 'Groups', 'Teams', 'Invited', ''].map((label) => (
                        <Box key={label || 'row'} sx={{ display: 'flex', justifyContent: label ? 'flex-start' : 'flex-end' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.68rem' }}>
                                {label || 'Row(s)'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                {renderReviewUserRows(
                    users,
                    VIRTUALIZED_NOOP_USER_ROW_HEIGHT,
                    (user, index, options) => (
                        <NoopUserRow
                            key={previewUserRowKey(user, index)}
                            user={user}
                            style={options?.style}
                            measureRef={options?.measureRef}
                        />
                    ),
                )}
            </CardContent>
        </Card>
    );

    const renderNewUsersSection = (users: PreviewUser[]) => (
        <Card
            sx={{
                ...summaryCardSx,
                borderColor: alpha('#0f172a', 0.08),
                backgroundColor: '#fff',
            }}
        >
            <CardContent sx={{ p: 0 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1}
                    sx={{ px: 2.2, pt: 2.2, pb: 1.3 }}
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.02rem' }}>
                            User creation plan ({users.length})
                        </Typography>
                    </Box>
                    {users.length > 20 && (
                        <Button
                            variant="text"
                            onClick={() => setShowAllNewUsers((current) => !current)}
                        >
                            {showAllNewUsers ? 'Show fewer rows' : 'Show all rows'}
                        </Button>
                    )}
                </Stack>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: newUsersTableColumns,
                        columnGap: 2,
                        px: 2.2,
                        py: 1.1,
                        borderTop: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: alpha('#0f172a', 0.08),
                        backgroundColor: alpha('#f8fafc', 0.8),
                    }}
                >
                    {['Name/Email', 'Role', 'Alert', 'Groups', 'Teams', 'Send Invite', ''].map((label) => (
                        <Box key={label || 'row'} sx={{ display: 'flex', justifyContent: label ? 'flex-start' : 'flex-end' }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.68rem' }}>
                                {label || 'Row(s)'}
                            </Typography>
                        </Box>
                    ))}
                </Box>
                {renderReviewUserRows(
                    displayedCreatedUsers,
                    VIRTUALIZED_NEW_USER_ROW_HEIGHT,
                    (user, index, options) => (
                        <NewUserRow
                            key={previewUserRowKey(user, index)}
                            user={user}
                            primaryMain={primaryMain}
                            style={options?.style}
                            measureRef={options?.measureRef}
                        />
                    ),
                )}
                {users.length > 20 && !showAllNewUsers && (
                    <Box
                        sx={{
                            py: 1.5,
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <Button
                            variant="text"
                            onClick={() => setShowAllNewUsers(true)}
                        >
                            Show all {users.length} users
                        </Button>
                    </Box>
                )}
            </CardContent>
        </Card>
    );

    const renderInvalidUsers = (users: InvalidUserEntry[]) => (
        <Card
            data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.invalidUsersSection}
            sx={{
                ...summaryCardSx,
                borderColor: alpha('#0f172a', 0.08),
                backgroundColor: '#fff',
            }}
        >
            <CardContent sx={{ p: 0 }}>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1}
                    sx={{ px: 2.2, pt: 2.2, pb: 1.3 }}
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.02rem' }}>
                            Invalid users ({users.length})
                        </Typography>
                    </Box>
                </Stack>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: invalidUsersTableColumns,
                        columnGap: 2,
                        px: 2.2,
                        py: 1.1,
                        borderTop: '1px solid',
                        borderBottom: '1px solid',
                        borderColor: alpha('#0f172a', 0.08),
                        backgroundColor: alpha('#f8fafc', 0.8),
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.68rem' }}>
                            Name/Email
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.68rem' }}>
                            Role
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.68rem' }}>
                            Group/Team
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.68rem' }}>
                            Row(s)
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.68rem' }}>
                            Validation Issue(s)
                        </Typography>
                    </Box>
                </Box>
                <Stack spacing={0}>
                    {users.map((user) => (
                        <InvalidUserAccordion
                            key={user.key}
                            user={user}
                            primaryMain={primaryMain}
                            primaryLight={primaryLight}
                        />
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );

    const renderStructurePreview = () => (
        <Card sx={summaryCardSx}>
            <CardContent>
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    spacing={1}
                    mb={1.5}
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.02rem' }}>
                            Groups and teams creation plan
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {previewData?.summary.groupsToCreate} {previewData?.summary.groupsToCreate === 1 ? 'group' : 'groups'} to create • {previewData?.summary.teamsToCreate} {previewData?.summary.teamsToCreate === 1 ? 'team' : 'teams'} to create
                        </Typography>
                    </Box>
                </Stack>
                <Stack spacing={2}>
                    {creationPlanGroups.length > 0 ? (
                        <Box>
                            <Grid container spacing={1.25}>
                                {creationPlanGroups.map((group) => (
                                    <Grid item xs={12} md={6} xl={4} key={group.name}>
                                        <Box
                                            sx={{
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: alpha('#0f172a', 0.1),
                                                backgroundColor: '#fff',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            <Box sx={{ px: 1.25, pt: 1.1, pb: 0.85 }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                                    <Box>
                                                        <Typography variant="body1" fontWeight={700} sx={{ fontSize: '0.88rem', lineHeight: 1.2, mb: 0.35 }}>
                                                            {group.name}
                                                        </Typography>
                                                        <Chip
                                                            size="small"
                                                            label={group.isNew ? 'NEW' : 'EXISTING'}
                                                            sx={{
                                                                fontWeight: 700,
                                                                height: 20,
                                                                fontSize: '0.62rem',
                                                                letterSpacing: '0.04em',
                                                                bgcolor: group.isNew ? primaryDark : alpha('#64748b', 0.1),
                                                                color: group.isNew ? '#fff' : alpha('#475569', 0.95),
                                                                '& .MuiChip-label': { px: 1 },
                                                            }}
                                                        />
                                                    </Box>
                                                    <GroupsRoundedIcon sx={{ color: alpha('#0f172a', 0.4), fontSize: 22 }} />
                                                </Stack>
                                            </Box>

                                            <Divider />

                                            <Box sx={{ px: 1.25, pt: 0.85, pb: 0.85 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.2 }}>
                                                    Group Leaders
                                                </Typography>
                                                {group.groupLeaders.length > 0 ? (
                                                    <Tooltip
                                                        title={group.groupLeaders.length > 2 ? formatLeaderNames(group.groupLeaders) : ''}
                                                        arrow
                                                        placement="top"
                                                    >
                                                        <Typography variant="body2" sx={{ fontSize: '0.82rem', cursor: 'default', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {group.groupLeaders.slice(0, 2).map((l) => `${l.firstName} ${l.lastName}`.trim()).join(', ')}
                                                            {group.groupLeaders.length > 2 && (
                                                                <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                                    {' '}+{group.groupLeaders.length - 2} more
                                                                </Box>
                                                            )}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.82rem' }}>
                                                        None assigned
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Box sx={{ px: 1.25, pb: 1.1 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
                                                    Teams ({group.teams.length})
                                                </Typography>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        ...(group.teams.length > 3 && {
                                                            maxHeight: 165,
                                                            overflowY: 'auto',
                                                            '&::-webkit-scrollbar': { width: 5 },
                                                            '&::-webkit-scrollbar-track': { bgcolor: alpha('#0f172a', 0.04), borderRadius: 3 },
                                                            '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#0f172a', 0.18), borderRadius: 3 },
                                                        }),
                                                    }}
                                                >
                                                    <Stack spacing={0.5}>
                                                        {group.teams.map((team) => (
                                                            <Box
                                                                key={`${group.name}-${team.name}`}
                                                                sx={{
                                                                    px: 1,
                                                                    py: 0.6,
                                                                    borderRadius: 1.5,
                                                                    border: '1px solid',
                                                                    borderColor: alpha('#0f172a', 0.08),
                                                                    backgroundColor: alpha('#f8fafc', 0.8),
                                                                }}
                                                            >
                                                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
                                                                    <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                                                                        {team.name}
                                                                    </Typography>
                                                                    <Chip
                                                                        size="small"
                                                                        label={team.isNew ? 'NEW' : 'EXISTING'}
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            height: 20,
                                                                            fontSize: '0.6rem',
                                                                            letterSpacing: '0.04em',
                                                                            bgcolor: team.isNew ? alpha(primaryDark, 0.12) : alpha('#64748b', 0.08),
                                                                            color: team.isNew ? primaryDark : alpha('#475569', 0.95),
                                                                            '& .MuiChip-label': { px: 0.7 },
                                                                            flexShrink: 0,
                                                                        }}
                                                                    />
                                                                </Stack>
                                                                <Tooltip
                                                                    title={team.teamLeaders.length > 2 ? formatLeaderNames(team.teamLeaders) : ''}
                                                                    arrow
                                                                    placement="top"
                                                                >
                                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.15, fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'default' }}>
                                                                        <Box component="span" fontWeight={600}>Leaders:</Box>{' '}
                                                                        {team.teamLeaders.length > 0
                                                                            ? <>
                                                                                {team.teamLeaders.slice(0, 2).map((l) => `${l.firstName} ${l.lastName}`.trim()).join(', ')}
                                                                                {team.teamLeaders.length > 2 && (
                                                                                    <Box component="span" sx={{ fontWeight: 600 }}>
                                                                                        {' '}+{team.teamLeaders.length - 2} more
                                                                                    </Box>
                                                                                )}
                                                                            </>
                                                                            : 'None'}
                                                                    </Typography>
                                                                </Tooltip>
                                                            </Box>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                                {group.teams.length > 3 && (
                                                    <Box
                                                        sx={{
                                                            mt: 0.75,
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        <ExpandMoreRoundedIcon sx={{ fontSize: 16 }} />
                                                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.02em' }}>
                                                            Scroll for more teams
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            No new groups or teams in this preview.
                        </Typography>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <Box
            data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.pageRoot}
            sx={{ pb: 0, width: '100%', position: 'relative', ...(step === 'upload' ? { height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' } : {}) }}
        >
            {isDragActive && (
                <Box
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 1600,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(180deg, rgba(15,23,42,0.24) 0%, rgba(15,23,42,0.34) 100%)',
                        backdropFilter: 'blur(10px)',
                        pointerEvents: 'none',
                    }}
                >
                    <Box
                        sx={{
                            width: 'min(560px, calc(100vw - 32px))',
                            borderRadius: 4,
                            backgroundColor: alpha('#ffffff', 0.94),
                            boxShadow: '0 30px 70px rgba(15, 23, 42, 0.18)',
                            border: '1px solid',
                            borderColor: alpha('#d5deea', 0.88),
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                px: { xs: 3, md: 4 },
                                py: { xs: 3, md: 3.5 },
                                background: 'linear-gradient(135deg, rgba(0,39,87,0.98) 0%, rgba(15,61,122,0.94) 100%)',
                                color: '#f8fafc',
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Box
                                    sx={{
                                        width: 56,
                                        height: 56,
                                        borderRadius: 3,
                                        display: 'grid',
                                        placeItems: 'center',
                                        backgroundColor: alpha('#ffffff', 0.12),
                                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
                                        flexShrink: 0,
                                    }}
                                >
                                    <CloudUploadRoundedIcon sx={{ fontSize: 28, color: '#cbe7f5' }} />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 800,
                                            letterSpacing: '-0.03em',
                                            lineHeight: 1.05,
                                            mb: 0.6,
                                        }}
                                    >
                                        {previewData?.previewId ? 'Replace this preview' : 'Upload this workbook'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: alpha('#e2ecfb', 0.9), lineHeight: 1.55 }}>
                                        {previewData?.previewId
                                            ? 'Drop the workbook here to update the preview with the latest file.'
                                            : 'Drop the workbook here to upload it and generate a preview.'}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        <Box sx={{ px: { xs: 3, md: 4 }, py: 3 }}>
                            <Box
                                sx={{
                                    borderRadius: 3,
                                    border: '2px dashed',
                                    borderColor: alpha('#8fc9ef', 0.9),
                                    background: 'linear-gradient(180deg, rgba(248,251,255,0.98) 0%, rgba(243,247,251,0.98) 100%)',
                                    px: 3,
                                    py: 2.4,
                                }}
                            >
                                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 2.5,
                                                backgroundColor: '#ffffff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <DescriptionOutlinedIcon sx={{ color: '#153d78', fontSize: 20 }} />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                                Ready to upload
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                Excel `.xlsx` files only
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    <Chip
                                        label={previewData?.previewId ? 'Update Preview' : 'Upload Workbook'}
                                        sx={{
                                            fontWeight: 700,
                                            backgroundColor: alpha('#0f3d7a', 0.08),
                                            color: '#0f3d7a',
                                            '& .MuiChip-label': { px: 1.4 },
                                        }}
                                    />
                                </Stack>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
            {/* file input is nested inside each Button component="label" below */}

            {step === 'upload' && renderPageHeader({
                title: 'Upload system users',
                subtitle: 'Upload your completed workbook to review staff details before any changes are made.',
            })}

            {step === 'upload' && (
                <Stack
                    data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.uploadPageRoot}
                    spacing={0}
                    sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                    <Box sx={{ overflow: 'hidden', px: 2, flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                        <Grid item xs={12} lg={8} sx={{ display: 'flex' }}>
                            <Card
                                sx={{
                                    ...summaryCardSx,
                                    borderRadius: 2,
                                    p: { xs: 0.75, md: 0.9 },
                                    display: 'flex',
                                    flex: 1,
                                    boxShadow: '0 22px 42px rgba(15, 23, 42, 0.06)',
                                }}
                            >
                                <CardContent sx={{ width: '100%', display: 'flex', p: '0 !important' }}>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            borderRadius: 2,
                                            border: '2px dashed',
                                            borderColor: alpha('#9fd6ff', 0.72),
                                            background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(251,253,255,0.92) 100%)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                            px: { xs: 2, md: 4 },
                                            py: { xs: 3.25, md: 4.5 },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 52,
                                                height: 52,
                                                borderRadius: 2,
                                                display: 'grid',
                                                placeItems: 'center',
                                                bgcolor: alpha('#7cc3ff', 0.22),
                                                color: '#153d78',
                                                mb: 1.6,
                                            }}
                                        >
                                            <CloudUploadRoundedIcon sx={{ fontSize: 24 }} />
                                        </Box>
                                        <Typography
                                            variant="h5"
                                            fontWeight={800}
                                            color="text.primary"
                                            sx={{
                                                mb: 1.15,
                                                fontSize: 'clamp(1.35rem, 1.45vw, 1.7rem)',
                                                letterSpacing: '-0.03em',
                                            }}
                                        >
                                            Choose workbook
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            color="text.secondary"
                                            sx={{
                                                maxWidth: '31rem',
                                                mb: 2.6,
                                                lineHeight: 1.45,
                                                fontSize: 'clamp(0.96rem, 0.92vw, 1.02rem)',
                                            }}
                                        >
                                            Upload the completed `.xlsx` workbook to review a live preview before any changes are made.
                                            Fill out the `System Users` sheet and leave any helper sheets in place.
                                        </Typography>
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4}>
                                            <Button
                                            component="label"
                                            size="large"
                                            variant="contained"
                                            startIcon={<CloudUploadRoundedIcon />}
                                            sx={{
                                                px: 2.25,
                                                py: 1.05,
                                                borderRadius: 1.5,
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                backgroundColor: '#206FA5',
                                                    boxShadow: '0 14px 28px rgba(32, 111, 165, 0.22)',
                                                    '&:hover': { backgroundColor: '#2b5a8a' },
                                                }}
                                            >
                                                Select .Xlsx File
                                                <input
                                                    data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.workbookInput}
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept=".xlsx"
                                                    hidden
                                                    onChange={handleFileSelection}
                                                />
                                            </Button>
                                            <LoadingButton
                                                size="large"
                                                variant="outlined"
                                                loading={isTemplateDownloadLoading}
                                                startIcon={!isTemplateDownloadLoading ? <FileDownloadOutlinedIcon /> : undefined}
                                                onClick={downloadTemplate}
                                                sx={{
                                                    px: 2.25,
                                                    py: 1.05,
                                                    borderRadius: 1.5,
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    borderColor: alpha('#206FA5', 0.35),
                                                    color: '#206FA5',
                                                    backgroundColor: alpha('#ffffff', 0.92),
                                                }}
                                            >
                                                Download Template
                                            </LoadingButton>
                                        </Stack>
                                        {selectedFileName && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                                mt={1.6}
                                                color="text.secondary"
                                                flexWrap="wrap"
                                                useFlexGap
                                            >
                                                <InsertDriveFileOutlinedIcon fontSize="small" />
                                                <Typography variant="body2">{selectedFileName}</Typography>
                                                <Button
                                                    variant="text"
                                                    color="inherit"
                                                    size="small"
                                                    startIcon={<CloseRoundedIcon fontSize="small" />}
                                                    onClick={clearSelectedFile}
                                                    sx={{
                                                        minWidth: 0,
                                                        px: 0.75,
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </Stack>
                                        )}
                                        {uploadError && (
                                            <Alert severity="error" sx={{ mt: 1.6, width: '100%', maxWidth: 560, textAlign: 'left', borderRadius: 2.5 }}>
                                                {uploadError}
                                            </Alert>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} lg={4} sx={{ display: 'flex' }}>
                            <Card
                                sx={{
                                    ...summaryCardSx,
                                    flex: 1,
                                    borderRadius: 2,
                                    boxShadow: '0 18px 36px rgba(15, 23, 42, 0.05)',
                                    background: 'linear-gradient(180deg, rgba(245,247,250,0.98) 0%, rgba(239,243,248,0.98) 100%)',
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2, md: 2.3 } }}>
                                    <Stack spacing={2.2} justifyContent="space-between" sx={{ height: '100%' }}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mb: 1.4, fontSize: '1.02rem' }}>
                                                Upload requirements
                                            </Typography>
                                            <Box
                                                component="ul"
                                                sx={{
                                                    m: 0,
                                                    p: 0,
                                                    listStyle: 'none',
                                                    display: 'grid',
                                                    rowGap: 1.45,
                                                }}
                                            >
                                                {[
                                                    'Upload an .xlsx workbook.',
                                                    `Workbook must be ${MAX_WORKBOOK_FILE_SIZE_MB} MB or smaller.`,
                                                    'Complete the System Users sheet.',
                                                    'You may leave helper sheets such as Notes and Allowed Values in the workbook.',
                                                    'All required headers must be included.',
                                                    'Validation issues will appear in the preview before any changes are made.',
                                                ].map((text) => (
                                                    <Box
                                                        key={text}
                                                        component="li"
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '10px minmax(0, 1fr)',
                                                            columnGap: 1.3,
                                                            alignItems: 'start',
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 7,
                                                                height: 7,
                                                                borderRadius: '50%',
                                                                bgcolor: '#5aaaf2',
                                                                mt: 'calc((1.42em - 7px) / 2)',
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                lineHeight: 1.42,
                                                            }}
                                                        >
                                                            {text}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 2,
                                                backgroundColor: alpha('#ffffff', 0.88),
                                                border: '1px solid',
                                                borderColor: alpha('#d5deea', 0.9),
                                            }}
                                        >
                                            <Stack direction="row" spacing={1} alignItems="flex-start">
                                                <InfoOutlinedIcon sx={{ color: primaryDark, fontSize: 18, mt: 0.15 }} />
                                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45, fontStyle: 'italic' }}>
                                                    Tip: Review the template&apos;s Help tab for specific formatting rules before uploading.
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                    </Box>

                    <Card
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            zIndex: 12,
                            borderRadius: 0,
                            border: 'none',
                            backgroundColor: alpha('#ffffff', 0.96),
                            backdropFilter: 'blur(14px)',
                            boxShadow: '0 -10px 30px rgba(0,0,0,0.03)',
                        }}
                    >
                        <CardContent
                            sx={{
                                px: { xs: 2, md: 4 },
                                pr: { xs: 2, md: 22 },
                                py: { xs: 1.1, md: 1.2 },
                                '&:last-child': {
                                    pb: { xs: 1.1, md: 1.2 },
                                },
                            }}
                        >
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1.2}
                                alignItems={{ xs: 'flex-start', md: 'center' }}
                                justifyContent={{ xs: 'flex-start', md: 'space-between' }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.3,
                                        visibility: selectedFileName ? 'visible' : 'hidden',
                                    }}
                                >
                                    {selectedFileName ? `${selectedFileName} selected` : 'placeholder'}
                                </Typography>
                                <Stack
                                    direction="row"
                                    spacing={1.2}
                                    sx={{
                                        width: { xs: '100%', md: 'auto' },
                                        justifyContent: { xs: 'space-between', md: 'flex-start' },
                                        mr: { md: 4 },
                                    }}
                                >
                                    <Button
                                        variant="text"
                                        color="inherit"
                                        onClick={() => navigate('/system-users')}
                                        sx={{ textTransform: 'none', fontWeight: 700 }}
                                    >
                                        Cancel
                                    </Button>
                                    <LoadingButton
                                        variant="contained"
                                        loading={isPreviewLoading}
                                        disabled={!selectedFile}
                                        onClick={runPreview}
                                        sx={{
                                            minWidth: 185,
                                            borderRadius: 1.2,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            backgroundColor: '#206FA5',
                                            boxShadow: '0 10px 24px rgba(32, 111, 165, 0.18)',
                                            '&:hover': { backgroundColor: '#2b5a8a' },
                                        }}
                                    >
                                        Continue to Review
                                    </LoadingButton>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            )}

            {step === 'review' && previewData && (
                <Stack
                    data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.reviewPageRoot}
                    spacing={3}
                    sx={{ pb: '72px' }}
                >
                    {renderReviewSummarySection(previewData.summary)}
                    {invalidUsers.length > 0 && renderInvalidUsers(invalidUsers)}
                    {updatedUsers.length > 0 && renderUpdates(updatedUsers)}
                    {noopUsers.length > 0 && renderNoopUsers(noopUsers)}
                    {createdUsers.length > 0 && renderNewUsersSection(createdUsers)}
                    {renderStructurePreview()}
                </Stack>
            )}

            {step === 'executing' && (
                <Box data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.executingPageRoot}>
                    {renderPageHeader({
                        title: 'Bulk Upload In Progress',
                        showFileName: true,
                        breadcrumbs: [
                            { label: 'System Users', onClick: () => requestExecutionNavigation(() => navigate('/system-users')) },
                            { label: 'Upload' },
                            { label: 'Executing' },
                        ],
                    })}

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: 'calc(100vh - 260px)',
                            position: 'relative',
                            overflow: 'hidden',
                            px: { xs: 2, md: 4 },
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                opacity: 0.4,
                                pointerEvents: 'none',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '-10%',
                                    left: '-10%',
                                    width: '40%',
                                    height: '40%',
                                    background: alpha('#abc7ff', 0.6),
                                    borderRadius: '50%',
                                    filter: 'blur(120px)',
                                }}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: '-10%',
                                    right: '-10%',
                                    width: '30%',
                                    height: '30%',
                                    background: alpha('#cbe7f5', 0.6),
                                    borderRadius: '50%',
                                    filter: 'blur(100px)',
                                }}
                            />
                        </Box>

                        <Stack alignItems="center" sx={{ maxWidth: 560, width: '100%', textAlign: 'center', zIndex: 1 }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    mb: 6,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: 96,
                                        height: 96,
                                        borderRadius: '50%',
                                        backgroundColor: alpha('#002757', 0.1),
                                        '@keyframes pulseRing': {
                                            '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                                            '50%': { transform: 'scale(1.1)', opacity: 0.3 },
                                            '100%': { transform: 'scale(0.8)', opacity: 0.5 },
                                        },
                                        animation: 'pulseRing 3s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                                    }}
                                />
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: 128,
                                        height: 128,
                                        borderRadius: '50%',
                                        backgroundColor: alpha('#002757', 0.05),
                                        '@keyframes pulseRingOuter': {
                                            '0%': { transform: 'scale(0.8)', opacity: 0.5 },
                                            '50%': { transform: 'scale(1.1)', opacity: 0.3 },
                                            '100%': { transform: 'scale(0.8)', opacity: 0.5 },
                                        },
                                        animation: 'pulseRingOuter 3s cubic-bezier(0.4, 0, 0.6, 1) infinite 1s',
                                    }}
                                />
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: '50%',
                                        backgroundColor: '#0f3d7a',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 8px 24px rgba(0,39,87,0.25)',
                                        position: 'relative',
                                        zIndex: 2,
                                    }}
                                >
                                    <CloudUploadRoundedIcon sx={{ color: '#86aaee', fontSize: 32 }} />
                                </Box>
                            </Box>

                            <Box aria-live="polite">
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#002757', letterSpacing: '-0.02em', mb: 2 }}>
                                    Executing bulk upload...
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#434750', lineHeight: 1.6, mb: 5, maxWidth: 420, mx: 'auto' }}>
                                    Setting up groups and teams, creating users, and sending invites. This may take a moment.
                                </Typography>
                            </Box>

                            <Box
                                data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.executingFileCard}
                                sx={{
                                    width: '100%',
                                    backgroundColor: '#f2f4f6',
                                    p: 2.5,
                                    px: 3,
                                    borderRadius: 8,
                                    border: '1px solid',
                                    borderColor: alpha('#c3c6d2', 0.2),
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: 2.5,
                                        backgroundColor: '#ffffff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                        flexShrink: 0,
                                    }}
                                >
                                    <DescriptionOutlinedIcon sx={{ color: '#002757', fontSize: 20 }} />
                                </Box>
                                <Box sx={{ overflow: 'hidden', textAlign: 'left' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#002757', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {selectedFileName || 'workbook.xlsx'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#434750', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Excel Spreadsheet
                                    </Typography>
                                </Box>
                                <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                                    {[0, 1, 2].map((i) => (
                                        <Box
                                            key={i}
                                            sx={{
                                                width: 6,
                                                height: 6,
                                                borderRadius: '50%',
                                                backgroundColor: '#002757',
                                                '@keyframes bounceDot': {
                                                    '0%, 80%, 100%': { transform: 'scale(0)' },
                                                    '40%': { transform: 'scale(1)' },
                                                },
                                                animation: `bounceDot 1.4s ease-in-out ${i * 0.16}s infinite both`,
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>

                            <Box sx={{ mt: 8, width: '100%', maxWidth: 360, mx: 'auto' }}>
                                <Box
                                    sx={{
                                        height: 6,
                                        width: '100%',
                                        backgroundColor: '#e6e8ea',
                                        borderRadius: 3,
                                        overflow: 'hidden',
                                        position: 'relative',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            bottom: 0,
                                            left: 0,
                                            backgroundColor: '#002757',
                                            borderRadius: 3,
                                            '@keyframes indeterminate': {
                                                '0%': { left: '-35%', right: '100%' },
                                                '60%': { left: '100%', right: '-90%' },
                                                '100%': { left: '100%', right: '-90%' },
                                            },
                                            animation: 'indeterminate 2.1s cubic-bezier(0.65, 0.815, 0.735, 0.395) infinite',
                                        }}
                                    />
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            bottom: 0,
                                            left: 0,
                                            backgroundColor: '#002757',
                                            borderRadius: 3,
                                            '@keyframes indeterminateShort': {
                                                '0%': { left: '-200%', right: '100%' },
                                                '60%': { left: '107%', right: '-8%' },
                                                '100%': { left: '107%', right: '-8%' },
                                            },
                                            animation: 'indeterminateShort 2.1s cubic-bezier(0.165, 0.84, 0.44, 1) infinite 1.15s',
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Stack>
                    </Box>
                </Box>
            )}

            {step === 'results' && (executeResult || executeError) && (() => {
                const counts = executeResult?.counts;
                const isSuccess = executeResult?.status === 'success';
                const isBlocked = isBlockedExecuteResult(executeResult);
                const isStructureFailure = executeResult?.status === 'failed' && executeResult.failedPhase === 'structure';
                const isPartialUsers = executeResult?.status === 'partial' && executeResult.failedPhase === 'users';
                const isPartialInvites = executeResult?.status === 'partial' && executeResult.failedPhase === 'invites';
                const failedUsers = executeResult?.failures.users || [];
                const failedInvites = executeResult?.failures.invites || [];
                const structureFailures = executeResult?.failures.structure || [];
                const failedGroupsCount = structureFailures.filter((failure) => failure.type === 'group').length;
                const failedTeamsCount = structureFailures.filter((failure) => failure.type === 'team').length;
                const createdStructureGroups = executeResult?.createdStructure?.groups || [];
                const createdStructureTeams = executeResult?.createdStructure?.teams || [];
                const successfulUsers = [...(executeResult?.createdUsers || []), ...(executeResult?.updatedUsers || [])];
                const totalSuccessful = counts ? counts.usersCreated + counts.usersUpdated : 0;
                const totalProcessed = counts ? totalSuccessful + counts.userFailures : 0;
                const totalImpact = counts ? counts.usersCreated + counts.usersUpdated + counts.groupsCreated + counts.teamsCreated + counts.invitesSent : 0;
                const totalStructureSucceeded = counts ? counts.groupsCreated + counts.teamsCreated : 0;
                const totalStructureProcessed = totalStructureSucceeded + structureFailures.length;
                const canCancelBlockedUpload = isBlocked && executeResult?.executionState === 'not_started';
                const resultsContainerSx = isBlocked
                    ? { maxWidth: 'none', mx: 0, mt: 4, px: { xs: 2.4, md: 4.8 }, position: 'relative', zIndex: 2, pb: '100px' }
                    : { maxWidth: 980, mx: 'auto', mt: 4, px: { xs: 2, md: 4 }, position: 'relative', zIndex: 2, pb: '100px' };

                return (
                    <Box data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.resultsPageRoot}>
                        {renderPageHeader({
                            title: 'Import Results',
                            showFileName: true,
                            breadcrumbs: [
                                { label: 'System Users', onClick: () => navigate('/system-users') },
                                { label: 'Upload' },
                                { label: 'Results' },
                            ],
                        })}

                        <Box sx={resultsContainerSx}>
                            {executeError && !executeResult && (
                                <Stack spacing={3}>
                                    <Box
                                        role="alert"
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 16px 40px rgba(15,23,42,0.08)',
                                            borderLeft: '4px solid',
                                            borderColor: 'error.main',
                                            p: 3,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 2,
                                        }}
                                    >
                                        <ErrorOutlineRoundedIcon sx={{ color: 'error.main', fontSize: 28, mt: 0.2 }} />
                                        <Box>
                                            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, color: '#93000a' }}>
                                                Something went wrong
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#93000a', mt: 0.5 }}>
                                                We could not confirm the final bulk upload result. Some changes may have already been applied.
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        component="pre"
                                        sx={{
                                            m: 0,
                                            p: 2.5,
                                            borderRadius: 3,
                                            bgcolor: '#111827',
                                            color: '#f8fafc',
                                            fontSize: '0.8rem',
                                            overflowX: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {executeError}
                                    </Box>
                                </Stack>
                            )}

                            {isBlocked && previewData && executeResult && (
                                <Stack spacing={3}>
                                    <Box
                                        role="alert"
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 16px 40px rgba(15,23,42,0.08)',
                                            borderLeft: '4px solid',
                                            borderColor: 'warning.main',
                                            p: 3,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 2,
                                        }}
                                    >
                                        <WarningAmberRoundedIcon sx={{ color: 'warning.main', fontSize: 28, mt: 0.2 }} />
                                        <Box>
                                            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, color: '#9a3412' }}>
                                                Execution blocked
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#9a3412', mt: 0.5 }}>
                                                The organization&apos;s data has changed since this preview was generated. New validation errors were found. Please review the updated preview and try again.
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {renderPreviewSummaryBar(previewData.summary, invalidUsers.length)}
                                    {invalidUsers.length > 0 && renderInvalidUsers(invalidUsers)}
                                    {updatedUsers.length > 0 && renderUpdates(updatedUsers)}
                                    {noopUsers.length > 0 && renderNoopUsers(noopUsers)}
                                    {createdUsers.length > 0 && renderNewUsersSection(createdUsers)}
                                    {renderStructurePreview()}
                                </Stack>
                            )}

                            {isStructureFailure && counts && (
                                <Stack spacing={3}>
                                    <Box
                                        role="alert"
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 16px 40px rgba(15,23,42,0.08)',
                                            borderLeft: '4px solid',
                                            borderColor: 'error.main',
                                            p: 3,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 2,
                                        }}
                                    >
                                        <ErrorOutlineRoundedIcon sx={{ color: 'error.main', fontSize: 28, mt: 0.2 }} />
                                        <Box>
                                            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, color: '#93000a' }}>
                                                We couldn't finish the upload
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#93000a', mt: 0.5 }}>
                                                We ran into a problem while creating groups or teams, so the upload stopped before any users were created or updated. Some groups or teams may already have been created.
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{ backgroundColor: '#ffffff', borderRadius: 3, border: '1px solid', borderColor: alpha('#c3c6d2', 0.1), boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', mb: 1, display: 'block' }}>
                                                    Total Structure Processed
                                                </Typography>
                                                <Typography sx={{ fontWeight: 800, fontSize: '2.25rem', color: '#002757', letterSpacing: '-0.04em' }}>
                                                    {totalStructureProcessed}
                                                </Typography>
                                                <Box sx={{ mt: 2, height: 4, width: '100%', backgroundColor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                                                    <Box sx={{ height: '100%', backgroundColor: '#002757', width: '100%', borderRadius: 2 }} />
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{ backgroundColor: '#ffffff', borderRadius: 3, border: '1px solid', borderColor: alpha('#c3c6d2', 0.1), boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', mb: 1, display: 'block' }}>
                                                    Created Before Failure
                                                </Typography>
                                                <Typography sx={{ fontWeight: 800, fontSize: '2.25rem', color: '#059669', letterSpacing: '-0.04em' }}>
                                                    {totalStructureSucceeded}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 1.25, color: '#4b5563' }}>
                                                    {counts.groupsCreated} {counts.groupsCreated === 1 ? 'group' : 'groups'}, {counts.teamsCreated} {counts.teamsCreated === 1 ? 'team' : 'teams'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{ backgroundColor: '#ffdad6', borderRadius: 3, border: '1px solid', borderColor: alpha('#ba1a1a', 0.1), boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: alpha('#93000a', 0.7), mb: 1, display: 'block' }}>
                                                    Structure Failed
                                                </Typography>
                                                <Typography sx={{ fontWeight: 800, fontSize: '2.25rem', color: '#ba1a1a', letterSpacing: '-0.04em' }}>
                                                    {structureFailures.length}
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 1.25, color: '#93000a' }}>
                                                    {failedGroupsCount} {failedGroupsCount === 1 ? 'group' : 'groups'}, {failedTeamsCount} {failedTeamsCount === 1 ? 'team' : 'teams'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Card sx={summaryCardSx}>
                                        <CardContent>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, px: 1 }}>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    <ErrorOutlineRoundedIcon sx={{ color: '#ba1a1a' }} />
                                                    <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                                        Failed structure items
                                                    </Typography>
                                                </Stack>
                                                <Chip
                                                    label={structureFailures.length}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: alpha('#ba1a1a', 0.1),
                                                        color: '#ba1a1a',
                                                        fontWeight: 700,
                                                        fontSize: '0.7rem',
                                                        letterSpacing: '0.03em',
                                                    }}
                                                />
                                            </Stack>
                                            <Box sx={{ backgroundColor: '#f2f4f6', borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                                {structureFailures.map((failure, index) => (
                                                    <Box
                                                        key={`${failure.type}-${failure.name}-${index}`}
                                                        tabIndex={0}
                                                        sx={{
                                                            backgroundColor: '#ffffff',
                                                            px: 2,
                                                            py: 1.75,
                                                            display: 'grid',
                                                            gap: 1.2,
                                                            borderBottom: index < structureFailures.length - 1 ? '1px solid' : 'none',
                                                            borderColor: alpha('#c3c6d2', 0.2),
                                                            '&:hover': { backgroundColor: '#f8fafc' },
                                                        }}
                                                    >
                                                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
                                                            <Chip
                                                                label={failure.type === 'group' ? 'Group' : 'Team'}
                                                                size="small"
                                                                sx={{
                                                                    alignSelf: 'flex-start',
                                                                    bgcolor: alpha('#ba1a1a', 0.1),
                                                                    color: '#ba1a1a',
                                                                    fontWeight: 700,
                                                                }}
                                                            />
                                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                <Typography variant="body1" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                                                    {failure.name}
                                                                </Typography>
                                                                {failure.type === 'team' && failure.groupName && (
                                                                    <Typography variant="caption" sx={{ color: '#6b7280' }}>
                                                                        In group {failure.groupName}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Stack>
                                                        <ExpandableExecuteError message={failure.error} />
                                                    </Box>
                                                ))}
                                            </Box>
                                            <Typography variant="body2" sx={{ mt: 1.5, color: '#6b7280' }}>
                                                No users were changed. No invites were sent.
                                            </Typography>
                                        </CardContent>
                                    </Card>

                                    <Box
                                        role="alert"
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: alpha('#002757', 0.05),
                                            p: 3,
                                            display: 'flex',
                                            gap: 2,
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <InfoOutlinedIcon sx={{ color: '#002757', fontSize: 24, mt: 0.2 }} />
                                        <Box>
                                            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, color: '#002757', mb: 0.7 }}>
                                                To try again, start a new upload
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.65 }}>
                                                This upload can't continue from this preview. Start a new upload to create a fresh preview, then try again.
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Accordion
                                        expanded={structureCompletedExpanded}
                                        onChange={(_, expanded) => setStructureCompletedExpanded(expanded)}
                                        sx={{ ...summaryCardSx, boxShadow: 'none', '&:before': { display: 'none' } }}
                                    >
                                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <CheckCircleRoundedIcon sx={{ color: '#059669' }} />
                                                <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                                    Completed before failure
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#434750' }}>
                                                    ({totalStructureSucceeded})
                                                </Typography>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            {createdStructureGroups.length > 0 || createdStructureTeams.length > 0 ? (
                                                <Box sx={{ backgroundColor: '#ffffff', borderTop: '1px solid', borderColor: alpha('#c3c6d2', 0.2) }}>
                                                    {createdStructureGroups.map((group, index) => (
                                                        <Box
                                                            key={`created-group-${group.name}-${index}`}
                                                            sx={{
                                                                p: 2,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                                borderBottom: '1px solid',
                                                                borderColor: alpha('#c3c6d2', 0.15),
                                                            }}
                                                        >
                                                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <BusinessRoundedIcon sx={{ color: '#059669', fontSize: 16 }} />
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#191c1e' }}>
                                                                    {group.name}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: '#434750' }}>
                                                                    Group created successfully
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                    {createdStructureTeams.map((team, index) => (
                                                        <Box
                                                            key={`created-team-${team.groupName}-${team.teamName}-${index}`}
                                                            sx={{
                                                                p: 2,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2,
                                                                borderBottom: index < createdStructureTeams.length - 1 ? '1px solid' : 'none',
                                                                borderColor: alpha('#c3c6d2', 0.15),
                                                            }}
                                                        >
                                                            <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <AccountTreeRoundedIcon sx={{ color: '#059669', fontSize: 16 }} />
                                                            </Box>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#191c1e' }}>
                                                                    {team.teamName}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: '#434750' }}>
                                                                    Team created in {team.groupName}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Box sx={{ p: 2.5, borderTop: '1px solid', borderColor: alpha('#c3c6d2', 0.2), backgroundColor: '#ffffff' }}>
                                                    <Typography variant="body2" sx={{ color: '#434750' }}>
                                                        {counts.groupsCreated} {counts.groupsCreated === 1 ? 'group' : 'groups'} created, {counts.teamsCreated} {counts.teamsCreated === 1 ? 'team' : 'teams'} created before execution stopped.
                                                    </Typography>
                                                </Box>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                </Stack>
                            )}

                            {isSuccess && counts && (
                                <Stack spacing={3.5}>
                                    <Grid container spacing={2} data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.resultsSummaryCards}>
                                        <Grid item xs={12} md={8}>
                                            <Box
                                                sx={{
                                                    backgroundColor: '#ffffff',
                                                    borderRadius: 4,
                                                    border: '1px solid',
                                                    borderColor: '#e6e8ea',
                                                    p: 4,
                                                    height: 192,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'space-between',
                                                }}
                                            >
                                                <Box>
                                                    <Typography component="h2" variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af', mb: 0.5, display: 'block' }}>
                                                        Total Impact
                                                    </Typography>
                                                    <Typography
                                                        data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.resultsTotalImpact}
                                                        sx={{ fontWeight: 800, fontSize: '2.25rem', color: '#002757', letterSpacing: '-0.04em' }}
                                                    >
                                                        {totalImpact}
                                                    </Typography>
                                                </Box>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: '#059669' }}>
                                                    <VerifiedRoundedIcon sx={{ fontSize: 20 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>All records processed successfully</Typography>
                                                </Stack>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} md={4}>
                                            <Box sx={{ backgroundColor: '#ffffff', borderRadius: 4, border: '1px solid', borderColor: '#e6e8ea', p: 3, height: 192, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', boxShadow: '0 20px 40px rgba(25,28,30,0.06)' }}>
                                                <Typography data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.resultsUsersCreated} sx={{ fontWeight: 700, fontSize: '2rem', color: '#002757' }}>{counts.usersCreated}</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280' }}>Users created</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Box sx={{ backgroundColor: '#f2f4f6', borderRadius: 4, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', height: 120 }}>
                                                <Typography data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.resultsUsersUpdated} sx={{ fontWeight: 700, fontSize: '2rem', color: '#002757' }}>{counts.usersUpdated}</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280' }}>Users updated</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Box sx={{ backgroundColor: '#f2f4f6', borderRadius: 4, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', height: 120 }}>
                                                <Typography data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.resultsGroupsCreated} sx={{ fontWeight: 700, fontSize: '2rem', color: '#002757' }}>{counts.groupsCreated}</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280' }}>Groups created</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Box sx={{ backgroundColor: '#f2f4f6', borderRadius: 4, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', height: 120 }}>
                                                <Typography data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.resultsTeamsCreated} sx={{ fontWeight: 700, fontSize: '2rem', color: '#002757' }}>{counts.teamsCreated}</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280' }}>Teams created</Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={6} md={3}>
                                            <Box sx={{ backgroundColor: '#ecfdf5', borderRadius: 4, border: '1px solid #a7f3d0', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', height: 120 }}>
                                                <Typography data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.resultsInvitesSent} sx={{ fontWeight: 700, fontSize: '2rem', color: '#047857' }}>{counts.invitesSent}</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#059669' }}>Invites sent</Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    <Box role="status" sx={{ backgroundColor: alpha('#0f3d7a', 0.06), borderRadius: 3, p: 4, display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                                        <InfoOutlinedIcon sx={{ color: '#0f3d7a', fontSize: 28, mt: 0.2 }} />
                                        <Box>
                                            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, color: '#002757', mb: 1 }}>
                                                Automated Provisioning Finished
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#434750', lineHeight: 1.7 }}>
                                                Automated provisioning finished successfully.
                                                {counts.invitesSent > 0 && ` ${counts.invitesSent} invitation email${counts.invitesSent === 1 ? ' has' : 's have'} been queued.`}
                                                {' '}Dashboard and system changes may take a short time to appear.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Stack>
                            )}

                            {isPartialUsers && counts && (
                                <Stack spacing={3}>
                                    <Box
                                        role="alert"
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 16px 40px rgba(15,23,42,0.08)',
                                            borderLeft: '4px solid',
                                            borderColor: '#f59e0b',
                                            p: 3,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 2,
                                        }}
                                    >
                                        <WarningAmberRoundedIcon sx={{ color: '#d97706', fontSize: 28, mt: 0.2 }} />
                                        <Box>
                                            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, color: '#78350f' }}>
                                                Part of the upload worked
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#92400e', mt: 0.5, fontWeight: 500 }}>
                                                We created or updated some users, but a few couldn&apos;t be completed. Groups and teams were set up successfully.
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{ backgroundColor: '#ffffff', borderRadius: 3, border: '1px solid', borderColor: alpha('#c3c6d2', 0.1), boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', mb: 1, display: 'block' }}>
                                                    Total Processed
                                                </Typography>
                                                <Typography sx={{ fontWeight: 800, fontSize: '2.25rem', color: '#002757', letterSpacing: '-0.04em' }}>
                                                    {totalProcessed}
                                                </Typography>
                                                <Box sx={{ mt: 2, height: 4, width: '100%', backgroundColor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                                                    <Box sx={{ height: '100%', backgroundColor: '#002757', width: '100%', borderRadius: 2 }} />
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{ backgroundColor: '#ffffff', borderRadius: 3, border: '1px solid', borderColor: alpha('#c3c6d2', 0.1), boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', mb: 1, display: 'block' }}>
                                                    Successful Imports
                                                </Typography>
                                                <Typography sx={{ fontWeight: 800, fontSize: '2.25rem', color: '#059669', letterSpacing: '-0.04em' }}>
                                                    {totalSuccessful}
                                                </Typography>
                                                <Box sx={{ mt: 2, height: 4, width: '100%', backgroundColor: '#f1f5f9', borderRadius: 2, overflow: 'hidden' }}>
                                                    <Box sx={{ height: '100%', backgroundColor: '#10b981', width: totalProcessed > 0 ? `${(totalSuccessful / totalProcessed) * 100}%` : '0%', borderRadius: 2 }} />
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <Box sx={{ backgroundColor: '#ffdad6', borderRadius: 3, border: '1px solid', borderColor: alpha('#ba1a1a', 0.1), boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: alpha('#93000a', 0.7), mb: 1, display: 'block' }}>
                                                    Users failed
                                                </Typography>
                                                <Typography sx={{ fontWeight: 800, fontSize: '2.25rem', color: '#ba1a1a', letterSpacing: '-0.04em' }}>
                                                    {counts.userFailures}
                                                </Typography>
                                                <Box sx={{ mt: 2, height: 4, width: '100%', backgroundColor: alpha('#ffffff', 0.5), borderRadius: 2, overflow: 'hidden' }}>
                                                    <Box sx={{ height: '100%', backgroundColor: '#ba1a1a', width: totalProcessed > 0 ? `${(counts.userFailures / totalProcessed) * 100}%` : '0%', borderRadius: 2 }} />
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>

                                    {failedUsers.length > 0 && (
                                        <Card sx={summaryCardSx}>
                                            <CardContent>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, px: 1 }}>
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <ErrorOutlineRoundedIcon sx={{ color: '#ba1a1a' }} />
                                                        <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                                            Failed users
                                                        </Typography>
                                                    </Stack>
                                                    <Chip
                                                        label={failedUsers.length}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: alpha('#ba1a1a', 0.1),
                                                            color: '#ba1a1a',
                                                            fontWeight: 700,
                                                            fontSize: '0.7rem',
                                                            letterSpacing: '0.03em',
                                                        }}
                                                    />
                                                </Stack>

                                                <Box sx={{ backgroundColor: '#f2f4f6', borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                                                    {failedUsers.map((user, index) => (
                                                        <Box
                                                            key={user.email + index}
                                                            tabIndex={0}
                                                            sx={{
                                                                p: 2,
                                                                backgroundColor: '#ffffff',
                                                                display: 'grid',
                                                                gap: 1.4,
                                                                borderBottom: index < failedUsers.length - 1 ? '1px solid' : 'none',
                                                                borderColor: alpha('#c3c6d2', 0.2),
                                                                '&:hover': { backgroundColor: '#f8fafc' },
                                                                transition: 'background-color 0.15s',
                                                            }}
                                                        >
                                                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
                                                                <Stack direction="row" spacing={2} alignItems="center">
                                                                    <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                                        <PersonOutlineRoundedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                                                                    </Box>
                                                                    <Box>
                                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                                                            {formatExecuteUserDisplayName(user)}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ color: '#434750' }}>
                                                                            {user.email}
                                                                        </Typography>
                                                                    </Box>
                                                                </Stack>
                                                                <Chip
                                                                    label={user.action === 'create' ? 'Create' : 'Update'}
                                                                    size="small"
                                                                    sx={{
                                                                        alignSelf: { xs: 'flex-start', md: 'center' },
                                                                        bgcolor: alpha('#002757', 0.08),
                                                                        color: '#002757',
                                                                        fontWeight: 700,
                                                                    }}
                                                                />
                                                            </Stack>
                                                            <ExpandableExecuteError message={user.error} />
                                                        </Box>
                                                    ))}
                                                </Box>
                                                <Typography variant="body2" sx={{ mt: 1.5, color: '#6b7280' }}>
                                                    Invites for failed users were not attempted during this run.
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    )}

                                    <Box
                                        role="status"
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: alpha('#002757', 0.05),
                                            p: 3,
                                            display: 'flex',
                                            gap: 2,
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <InfoOutlinedIcon sx={{ color: '#002757', fontSize: 24, mt: 0.2 }} />
                                        <Box>
                                            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, color: '#002757', mb: 0.7 }}>
                                                Want to review before trying again?
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.65 }}>
                                                You can go back to the preview screen for this upload, review the data again, and then retry the failed users when you&apos;re ready.
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Accordion expanded={succeededExpanded} onChange={(_, expanded) => setSucceededExpanded(expanded)} sx={{ ...summaryCardSx, boxShadow: 'none', '&:before': { display: 'none' } }}>
                                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <CheckCircleRoundedIcon sx={{ color: '#059669' }} />
                                                <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                                    Succeeded Users
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#434750' }}>
                                                    ({totalSuccessful})
                                                </Typography>
                                            </Stack>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ p: 0 }}>
                                            <Box sx={{ backgroundColor: '#ffffff', borderTop: '1px solid', borderColor: alpha('#c3c6d2', 0.2) }}>
                                                {successfulUsers.map((user, index) => (
                                                    <Box
                                                        key={user.email + index}
                                                        sx={{
                                                            p: 2,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            borderBottom: index < successfulUsers.length - 1 ? '1px solid' : 'none',
                                                            borderColor: alpha('#c3c6d2', 0.15),
                                                        }}
                                                    >
                                                        <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <CheckCircleRoundedIcon sx={{ color: '#059669', fontSize: 16 }} />
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#191c1e' }}>
                                                                {formatExecuteUserDisplayName(user)}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#434750' }}>{user.email}</Typography>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                </Stack>
                            )}

                            {isPartialInvites && counts && (
                                <Stack spacing={3}>
                                    <Box
                                        role="alert"
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: '#ffffff',
                                            boxShadow: '0 16px 40px rgba(15,23,42,0.08)',
                                            borderLeft: '4px solid',
                                            borderColor: '#f59e0b',
                                            p: 3,
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 2,
                                        }}
                                    >
                                        <InfoOutlinedIcon sx={{ color: '#b45309', fontSize: 28, mt: 0.2 }} />
                                        <Box>
                                            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, color: '#78350f' }}>
                                                Users were created, but some invites failed
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#92400e', mt: 0.5 }}>
                                                The upload finished, but some invitation emails couldn&apos;t be sent.
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Grid container spacing={2}>
                                        {[
                                            { label: 'Users created', value: counts.usersCreated, tone: 'neutral' },
                                            { label: 'Users updated', value: counts.usersUpdated, tone: 'neutral' },
                                            { label: 'Groups created', value: counts.groupsCreated, tone: 'neutral' },
                                            { label: 'Teams created', value: counts.teamsCreated, tone: 'neutral' },
                                            { label: 'Invites sent', value: counts.invitesSent, tone: 'success' },
                                            { label: 'Invites failed', value: counts.inviteFailures, tone: 'warning' },
                                        ].map((card) => (
                                            <Grid item xs={6} md={4} key={card.label}>
                                                <Box
                                                    sx={{
                                                        backgroundColor: card.tone === 'warning'
                                                            ? alpha('#f59e0b', 0.12)
                                                            : card.tone === 'success'
                                                              ? '#ecfdf5'
                                                              : '#ffffff',
                                                        borderRadius: 4,
                                                        border: '1px solid',
                                                        borderColor: card.tone === 'warning'
                                                            ? alpha('#f59e0b', 0.25)
                                                            : card.tone === 'success'
                                                              ? '#a7f3d0'
                                                              : '#e6e8ea',
                                                        p: 3,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        textAlign: 'center',
                                                        minHeight: 126,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: '2rem',
                                                            color: card.tone === 'warning'
                                                                ? '#b45309'
                                                                : card.tone === 'success'
                                                                  ? '#047857'
                                                                  : '#002757',
                                                        }}
                                                    >
                                                        {card.value}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            fontWeight: 700,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.04em',
                                                            color: card.tone === 'warning'
                                                                ? '#b45309'
                                                                : card.tone === 'success'
                                                                  ? '#059669'
                                                                  : '#6b7280',
                                                        }}
                                                    >
                                                        {card.label}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>

                                    <Card sx={summaryCardSx}>
                                        <CardContent>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                                                <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                    Failed invites
                                                </Typography>
                                                <Chip
                                                    label={failedInvites.length}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: alpha('#f59e0b', 0.14),
                                                        color: '#b45309',
                                                        fontWeight: 700,
                                                    }}
                                                />
                                            </Stack>
                                            <Stack spacing={1.25}>
                                                {failedInvites.map((invite, index) => (
                                                    <Box
                                                        key={`${invite.email}-${index}`}
                                                        tabIndex={0}
                                                        sx={{
                                                            borderRadius: 2,
                                                            border: '1px solid',
                                                            borderColor: alpha('#f59e0b', 0.18),
                                                            px: 2,
                                                            py: 1.5,
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#191c1e', mb: 0.8 }}>
                                                            {invite.email}
                                                        </Typography>
                                                        <ExpandableExecuteError message={invite.error} color="#b45309" />
                                                    </Box>
                                                ))}
                                            </Stack>
                                            <Typography variant="body2" sx={{ mt: 1.5, color: '#6b7280' }}>
                                                You can resend invites individually from each user&apos;s profile in the System Users page.
                                            </Typography>
                                        </CardContent>
                                    </Card>

                                    <Box
                                        role="status"
                                        sx={{
                                            borderRadius: 3,
                                            backgroundColor: alpha('#002757', 0.05),
                                            p: 3,
                                            display: 'flex',
                                            gap: 2,
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <InfoOutlinedIcon sx={{ color: '#002757', fontSize: 24, mt: 0.2 }} />
                                        <Box>
                                            <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, color: '#002757', mb: 0.7 }}>
                                                Want to review before retrying?
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.65 }}>
                                                You can go back to the preview screen for this upload, review the data again, and then retry the failed invites when you&apos;re ready.
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Stack>
                            )}
                        </Box>

                        <Card
                            sx={{
                                position: 'fixed',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                zIndex: 40,
                                borderRadius: 0,
                                border: 'none',
                                borderTop: '1px solid',
                                borderColor: alpha('#e2e8f0', 0.8),
                                backgroundColor: alpha('#ffffff', 0.9),
                                backdropFilter: 'blur(12px)',
                                boxShadow: '0 -4px 20px rgba(0,0,0,0.03)',
                            }}
                        >
                            <CardContent
                                sx={{
                                    px: { xs: 2, md: 6 },
                                    pr: { xs: 2, md: 12 },
                                    py: { xs: 1.5, md: 1.5 },
                                    '&:last-child': { pb: { xs: 1.5, md: 1.5 } },
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="caption" sx={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', display: { xs: 'none', md: 'block' } }}>
                                    {customerName}
                                </Typography>
                                <Stack
                                    data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.resultsActions}
                                    direction="row"
                                    spacing={2}
                                    alignItems="center"
                                >
                                    {isSuccess && (
                                        <>
                                            <Button onClick={() => clearPreviewState()} sx={{ textTransform: 'none', fontWeight: 700, color: '#002757', '&:hover': { backgroundColor: '#f1f5f9' } }}>
                                                Upload another workbook
                                            </Button>
                                            <Button
                                                variant="contained"
                                                endIcon={<ArrowForwardRoundedIcon />}
                                                onClick={() => navigate('/system-users')}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    borderRadius: 3,
                                                    px: 3,
                                                    backgroundColor: '#002757',
                                                    boxShadow: '0 4px 14px rgba(0,39,87,0.15)',
                                                    '&:hover': { backgroundColor: '#0f3d7a' },
                                                }}
                                            >
                                                Go to System Users
                                            </Button>
                                        </>
                                    )}

                                    {isBlocked && executeResult?.refreshedPreview && (
                                        <>
                                            {canCancelBlockedUpload && (
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => setCancelModalOpen(true)}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontWeight: 700,
                                                        borderRadius: 3,
                                                        px: 3,
                                                        borderColor: '#f59e0b',
                                                        color: '#9a3412',
                                                        '&:hover': { borderColor: '#d97706', backgroundColor: alpha('#f59e0b', 0.06) },
                                                    }}
                                                >
                                                    Cancel upload
                                                </Button>
                                            )}
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    if (!executeResult.refreshedPreview) {
                                                        toast.error('The updated preview is no longer available. Please re-run the preview.');
                                                        return;
                                                    }

                                                    applyPreviewResult(executeResult.refreshedPreview);
                                                }}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    borderRadius: 3,
                                                    px: 3,
                                                    backgroundColor: '#002757',
                                                    '&:hover': { backgroundColor: '#0f3d7a' },
                                                }}
                                            >
                                                Review updated preview
                                            </Button>
                                        </>
                                    )}

                                    {isStructureFailure && (
                                        <>
                                            <Button onClick={() => clearPreviewState()} sx={{ textTransform: 'none', fontWeight: 700, color: '#002757', '&:hover': { backgroundColor: '#f1f5f9' } }}>
                                                Start new upload
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={() => navigate('/system-users')}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    borderRadius: 3,
                                                    px: 3,
                                                    borderColor: '#cbe7f5',
                                                    backgroundColor: '#cbe7f5',
                                                    color: '#304a55',
                                                    '&:hover': { backgroundColor: '#afcbd8', borderColor: '#afcbd8' },
                                                }}
                                            >
                                                Go to System Users
                                            </Button>
                                        </>
                                    )}

                                    {isPartialUsers && (
                                        <>
                                            <Button
                                                variant="outlined"
                                                onClick={rerunPreviewFromResults}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    borderRadius: 3,
                                                    px: 3,
                                                    borderColor: '#cbe7f5',
                                                    backgroundColor: '#cbe7f5',
                                                    color: '#304a55',
                                                    '&:hover': { backgroundColor: '#afcbd8', borderColor: '#afcbd8' },
                                                }}
                                            >
                                                Review preview again
                                            </Button>
                                            <LoadingButton
                                                variant="contained"
                                                loading={isExecuting}
                                                startIcon={!isExecuting ? <RefreshRoundedIcon /> : undefined}
                                                onClick={runExecute}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    borderRadius: 3,
                                                    px: 3,
                                                    backgroundColor: '#002757',
                                                    '&:hover': { backgroundColor: '#0f3d7a' },
                                                }}
                                            >
                                                Retry failed users
                                            </LoadingButton>
                                            <Button
                                                onClick={() => navigate('/system-users')}
                                                sx={{ textTransform: 'none', fontWeight: 700, color: '#002757', '&:hover': { backgroundColor: '#f1f5f9' } }}
                                            >
                                                Go to System Users
                                            </Button>
                                        </>
                                    )}

                                    {isPartialInvites && (
                                        <>
                                            <Button
                                                variant="outlined"
                                                onClick={rerunPreviewFromResults}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    borderRadius: 3,
                                                    px: 3,
                                                    borderColor: '#cbe7f5',
                                                    backgroundColor: '#cbe7f5',
                                                    color: '#304a55',
                                                    '&:hover': { backgroundColor: '#afcbd8', borderColor: '#afcbd8' },
                                                }}
                                            >
                                                Review preview again
                                            </Button>
                                            <LoadingButton
                                                variant="contained"
                                                loading={isExecuting}
                                                startIcon={!isExecuting ? <RefreshRoundedIcon /> : undefined}
                                                onClick={runExecute}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    borderRadius: 3,
                                                    px: 3,
                                                    backgroundColor: '#002757',
                                                    '&:hover': { backgroundColor: '#0f3d7a' },
                                                }}
                                            >
                                                Retry failed invites
                                            </LoadingButton>
                                            <Button
                                                onClick={() => navigate('/system-users')}
                                                sx={{ textTransform: 'none', fontWeight: 700, color: '#002757', '&:hover': { backgroundColor: '#f1f5f9' } }}
                                            >
                                                Go to System Users
                                            </Button>
                                        </>
                                    )}

                                    {executeError && !executeResult && (
                                        <>
                                            <Button
                                                variant="outlined"
                                                onClick={() => navigate('/system-users')}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    borderRadius: 3,
                                                    px: 3,
                                                    borderColor: '#cbe7f5',
                                                    backgroundColor: '#cbe7f5',
                                                    color: '#304a55',
                                                    '&:hover': { backgroundColor: '#afcbd8', borderColor: '#afcbd8' },
                                                }}
                                            >
                                                Go to System Users
                                            </Button>
                                            <LoadingButton
                                                variant="contained"
                                                loading={isPreviewLoading}
                                                onClick={rerunPreviewFromResults}
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    borderRadius: 3,
                                                    px: 3,
                                                    backgroundColor: '#002757',
                                                    '&:hover': { backgroundColor: '#0f3d7a' },
                                                }}
                                            >
                                                Re-run preview
                                            </LoadingButton>
                                        </>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Box>
                );
            })()}

            <Dialog
                open={executionLeaveDialogOpen}
                onClose={() => {
                    setExecutionLeaveDialogOpen(false);
                    pendingExecutionNavigationRef.current = null;
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px rgba(25, 28, 30, 0.12)',
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: alpha('#191c1e', 0.4),
                            backdropFilter: 'blur(8px)',
                        },
                    },
                }}
            >
                <Box sx={{ p: 4 }}>
                    <Stack direction="row" spacing={2.5} alignItems="flex-start">
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 3,
                                backgroundColor: '#dbeafe',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <InfoOutlinedIcon sx={{ color: '#1d4ed8', fontSize: 24 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: '#191c1e',
                                    letterSpacing: '-0.01em',
                                    mb: 1,
                                }}
                            >
                                Leave while execution is running?
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: '#434750', lineHeight: 1.6 }}
                            >
                                Execution is in progress. Leaving this page won&apos;t cancel the operation, but you won&apos;t see the results here.
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
                <Box
                    sx={{
                        backgroundColor: '#f2f4f6',
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                    }}
                >
                    <Button
                        onClick={() => {
                            setExecutionLeaveDialogOpen(false);
                            pendingExecutionNavigationRef.current = null;
                        }}
                        sx={{
                            px: 3,
                            py: 1.2,
                            borderRadius: 3,
                            fontWeight: 700,
                            textTransform: 'none',
                            color: '#434750',
                            '&:hover': {
                                backgroundColor: '#e0e3e5',
                            },
                        }}
                    >
                        Stay on page
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setExecutionLeaveDialogOpen(false);
                            const pendingNavigation = pendingExecutionNavigationRef.current;
                            pendingExecutionNavigationRef.current = null;
                            if (!pendingNavigation) {
                                return;
                            }

                            executionNavigationBypassRef.current = true;
                            pendingNavigation();
                            window.setTimeout(() => {
                                executionNavigationBypassRef.current = false;
                            }, 250);
                        }}
                        sx={{
                            px: 4,
                            py: 1.2,
                            borderRadius: 3,
                            fontWeight: 700,
                            textTransform: 'none',
                            backgroundColor: '#206FA5',
                            boxShadow: '0 4px 14px rgba(32,111,165,0.25)',
                            '&:hover': {
                                backgroundColor: '#2b5a87',
                                boxShadow: '0 6px 20px rgba(32,111,165,0.35)',
                            },
                        }}
                    >
                        Leave anyway
                    </Button>
                </Box>
            </Dialog>

            {step === 'review' && previewData && (
                <Card
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 12,
                        borderRadius: 0,
                        border: 'none',
                        backgroundColor: alpha('#ffffff', 0.96),
                        backdropFilter: 'blur(14px)',
                        boxShadow: '0 -10px 30px rgba(0,0,0,0.03)',
                    }}
                >
                    <CardContent
                        sx={{
                            px: { xs: 2, md: 4 },
                            pr: { xs: 2, md: 22 },
                            py: { xs: 1.1, md: 1.2 },
                            '&:last-child': {
                                pb: { xs: 1.1, md: 1.2 },
                            },
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1.2}
                            justifyContent="flex-end"
                            alignItems="center"
                            sx={{ mr: { md: 4 } }}
                        >
                            <Button
                                variant="outlined"
                                color="inherit"
                                startIcon={<ArrowBackRoundedIcon sx={{ color: '#206FA5' }} />}
                                onClick={() => setCancelModalOpen(true)}
                                disabled={isCancellingPreview}
                                sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                                Cancel upload
                            </Button>
                            <Button
                                component="label"
                                variant="outlined"
                                color="inherit"
                                startIcon={<CloudUploadRoundedIcon sx={{ color: '#206FA5' }} />}
                                disabled={isPreviewLoading}
                                sx={{ textTransform: 'none', fontWeight: 700 }}
                            >
                                Replace workbook
                                <input
                                    type="file"
                                    accept=".xlsx"
                                    hidden
                                    onChange={handleFileSelection}
                                />
                            </Button>
                            <Button
                                data-testid={SYSTEM_USER_BULK_UPLOAD_TEST_IDS.executeButton}
                                variant="contained"
                                disabled={previewData.hasBlockingErrors || isPreviewLoading || isCancellingPreview}
                                startIcon={<RocketLaunchRoundedIcon />}
                                onClick={() => setExecuteConfirmOpen(true)}
                                sx={{
                                    px: 3,
                                    borderRadius: 1.2,
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    backgroundColor: previewData.hasBlockingErrors ? alpha('#206FA5', 0.4) : '#206FA5',
                                    color: alpha('#ffffff', 0.9),
                                    boxShadow: previewData.hasBlockingErrors ? 'none' : '0 4px 14px rgba(32,111,165,0.25)',
                                    '&:hover': {
                                        backgroundColor: '#2b5a87',
                                        boxShadow: '0 6px 20px rgba(32,111,165,0.35)',
                                    },
                                    '&.Mui-disabled': {
                                        backgroundColor: alpha('#206FA5', 0.4),
                                        color: alpha('#ffffff', 0.9),
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                {previewData.hasBlockingErrors ? 'Resolve errors to execute' : 'Execute'}
                            </Button>
                        </Stack>
                    </CardContent>
                </Card>
            )}

            <Dialog
                open={replaceModalOpen}
                onClose={() => !isPreviewLoading && setReplaceModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px rgba(25, 28, 30, 0.12)',
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: alpha('#191c1e', 0.4),
                            backdropFilter: 'blur(8px)',
                        },
                    },
                }}
            >
                <Box sx={{ p: 4 }}>
                    <Stack direction="row" spacing={2.5} alignItems="flex-start">
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 3,
                                backgroundColor: '#0f3d7a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <SwapHorizRoundedIcon sx={{ color: '#86aaee', fontSize: 24 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: '#191c1e',
                                    letterSpacing: '-0.01em',
                                    mb: 1,
                                }}
                            >
                                Replace preview workbook?
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: '#434750', lineHeight: 1.6 }}
                            >
                                Replace the current preview with <strong>{pendingReplacementFile?.name || 'this workbook'}</strong>? The preview will be recalculated using the improved file.
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
                <Box
                    sx={{
                        backgroundColor: '#f2f4f6',
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                    }}
                >
                    <Button
                        onClick={() => {
                            setReplaceModalOpen(false);
                            setPendingReplacementFile(null);
                        }}
                        disabled={isPreviewLoading}
                        sx={{
                            px: 3,
                            py: 1.2,
                            borderRadius: 3,
                            fontWeight: 700,
                            textTransform: 'none',
                            color: '#434750',
                            '&:hover': {
                                backgroundColor: '#e0e3e5',
                            },
                        }}
                    >
                        Keep Current Preview
                    </Button>
                    <LoadingButton
                        onClick={confirmReplacePreview}
                        loading={isPreviewLoading}
                        variant="contained"
                        sx={{
                            px: 4,
                            py: 1.2,
                            borderRadius: 3,
                            fontWeight: 700,
                            textTransform: 'none',
                            backgroundColor: '#206FA5',
                            boxShadow: '0 4px 14px rgba(32,111,165,0.25)',
                            '&:hover': {
                                backgroundColor: '#2b5a87',
                                boxShadow: '0 6px 20px rgba(32,111,165,0.35)',
                            },
                        }}
                    >
                        Replace Preview
                    </LoadingButton>
                </Box>
            </Dialog>

            <Dialog
                open={cancelModalOpen}
                onClose={() => !isCancellingPreview && setCancelModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px rgba(25, 28, 30, 0.12)',
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: alpha('#191c1e', 0.4),
                            backdropFilter: 'blur(8px)',
                        },
                    },
                }}
            >
                <Box sx={{ p: 4 }}>
                    <Stack direction="row" spacing={2.5} alignItems="flex-start">
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 3,
                                backgroundColor: '#fef3c7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <WarningAmberRoundedIcon sx={{ color: '#d97706', fontSize: 24 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: '#191c1e',
                                    letterSpacing: '-0.01em',
                                    mb: 1,
                                }}
                            >
                                Cancel upload?
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: '#434750', lineHeight: 1.6 }}
                            >
                                This will stop the current preview. You will need to upload the workbook again to continue.
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
                <Box
                    sx={{
                        backgroundColor: '#f2f4f6',
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                    }}
                >
                    <Button
                        onClick={() => setCancelModalOpen(false)}
                        disabled={isCancellingPreview}
                        sx={{
                            px: 3,
                            py: 1.2,
                            borderRadius: 3,
                            fontWeight: 700,
                            textTransform: 'none',
                            color: '#434750',
                            '&:hover': {
                                backgroundColor: '#e0e3e5',
                            },
                        }}
                    >
                        Keep Preview
                    </Button>
                    <LoadingButton
                        onClick={confirmCancelUpload}
                        loading={isCancellingPreview}
                        variant="contained"
                        sx={{
                            px: 4,
                            py: 1.2,
                            borderRadius: 3,
                            fontWeight: 700,
                            textTransform: 'none',
                            backgroundColor: '#d97706',
                            boxShadow: '0 4px 14px rgba(217,119,6,0.25)',
                            '&:hover': {
                                backgroundColor: '#b45309',
                                boxShadow: '0 6px 20px rgba(217,119,6,0.35)',
                            },
                        }}
                    >
                        Cancel Upload
                    </LoadingButton>
                </Box>
            </Dialog>

            <Dialog
                open={executeConfirmOpen}
                onClose={() => setExecuteConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    'data-testid': SYSTEM_USER_BULK_UPLOAD_TEST_IDS.executeDialog,
                    sx: {
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 20px 40px rgba(25, 28, 30, 0.12)',
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: alpha('#191c1e', 0.4),
                            backdropFilter: 'blur(8px)',
                        },
                    },
                }}
            >
                <Box sx={{ p: 4 }}>
                    <Stack direction="row" spacing={2.5} alignItems="flex-start">
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 3,
                                backgroundColor: '#0f3d7a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <CloudUploadRoundedIcon sx={{ color: '#86aaee', fontSize: 24 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    color: '#191c1e',
                                    letterSpacing: '-0.01em',
                                    mb: 1,
                                }}
                            >
                                Execute bulk upload?
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: '#434750', lineHeight: 1.6, mb: 2 }}
                            >
                                This will create {previewData?.summary.usersToCreate ?? 0} users,
                                update {previewData?.summary.usersToUpdate ?? 0} users,
                                create {previewData?.summary.groupsToCreate ?? 0} groups,{' '}
                                {previewData?.summary.teamsToCreate ?? 0} teams, and
                                send {previewData?.summary.invitesToSend ?? 0} invites.
                                This action cannot be undone.
                            </Typography>
                            <Grid
                                container
                                spacing={1.5}
                                sx={{
                                    py: 2,
                                    borderTop: '1px solid',
                                    borderBottom: '1px solid',
                                    borderColor: alpha('#c3c6d2', 0.2),
                                }}
                            >
                                <Grid item xs={6}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#002757' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                            {(previewData?.summary.usersToCreate ?? 0) + (previewData?.summary.usersToUpdate ?? 0)} Users
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={6}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#002757' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                            {previewData?.summary.groupsToCreate ?? 0} Groups
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={6}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#002757' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                            {previewData?.summary.teamsToCreate ?? 0} Teams
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={6}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#002757' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#191c1e' }}>
                                            {previewData?.summary.invitesToSend ?? 0} Invites
                                        </Typography>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>
                    </Stack>
                </Box>
                <Box
                    sx={{
                        backgroundColor: '#f2f4f6',
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                    }}
                >
                    <Button
                        onClick={() => setExecuteConfirmOpen(false)}
                        sx={{
                            px: 3,
                            py: 1.2,
                            borderRadius: 3,
                            fontWeight: 700,
                            textTransform: 'none',
                            color: '#434750',
                            '&:hover': {
                                backgroundColor: '#e0e3e5',
                            },
                        }}
                    >
                        Go back
                    </Button>
                    <Button
                        variant="contained"
                        disabled={previewData?.hasBlockingErrors}
                        onClick={() => {
                            setExecuteConfirmOpen(false);
                            runExecute();
                        }}
                        sx={{
                            px: 4,
                            py: 1.2,
                            borderRadius: 3,
                            fontWeight: 700,
                            textTransform: 'none',
                            backgroundColor: '#206FA5',
                            boxShadow: '0 4px 14px rgba(32,111,165,0.25)',
                            '&:hover': {
                                backgroundColor: '#2b5a87',
                                boxShadow: '0 6px 20px rgba(32,111,165,0.35)',
                            },
                            '&.Mui-disabled': {
                                backgroundColor: alpha('#206FA5', 0.4),
                                color: alpha('#ffffff', 0.9),
                            },
                        }}
                    >
                        Execute
                    </Button>
                </Box>
            </Dialog>
        </Box>
    );
};
